import { applyAttackStyle } from './coreAttackStyles';
import { createBoardCameraFramer } from './boardCameraFraming';
import { createBoardDieCamera } from './boardDieCamera';
import { getBoardPixelRatio } from './boardRenderBudget';
import { disposeCanvasTexture, disposeSceneResources } from './sceneResources';
import {
  usesMobileRendering,
  shouldPauseBoardRendering,
} from './mobileRendering';
import {
  CORE_BOARD_POSITION,
  CORE_BOARD_STRIKE_TARGET,
  CORE_BOARD_FRAME_RADIUS,
  CORE_BOARD_FRAME_POINTS,
} from './coreBoardLayout';
import { CORE_ATTACKS, rollCoreDamage } from './coreDamage';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import * as THREE from 'three';
import { createCoreboundRenderer } from './createCoreboundRenderer';
import FracturedCoreBackdrop from './FracturedCoreBackdrop';
import { createSectorEnvironment } from './sectorEnvironment';
import SectorEnemy from './SectorEnemy';
import DamageHitSplat from './DamageHitSplat';
import { createGuardianScene, GUARDIAN_POSITION } from './guardianScene';
import { createCoreBoardScene } from './coreBoardScene';
import { getCoreSpellProgress, getCoreSpellRevealAt } from './coreBoardPhases';
import { getCoreLaserDuration } from './coreLaser';
import GlitchCoreScene from './GlitchCoreScene';
import CanvasHealthBars from './CanvasHealthBars';
import './CoreBoardBoss.css';
import { createGuardianAttackCamera } from './guardianCamera';
import { createGuardianCounterScene } from './guardianAttackScene';
import { createGuardianStrikeScene } from './guardianStrikeScene';
import { getGuardianCounterAttack } from './sectorEnemies';
import { observeElementSize } from '../../utils/observeElementSize';
import { formatDieValue, getDie, rollDie } from './dice';
import { paintDieFace } from './dieFaceSkin';
import {
  getLabelContext,
  createLabelTexture,
  updateLabelTexture,
} from './labelTextures';
import { drawCoreboundIcon } from './coreboundIconArt';
import { getCoreDieAppearance } from './coreDieAppearance';
import { createCoreDieScene } from './coreDieScene';
import CoreDieFallback from './CoreDieFallback';
import {
  BoardEffectTile,
  BoardShieldIcon,
  isBoardEffectTile,
  drawBoardHeart,
  drawBoardShield,
} from './BoardEffectIcons';
import {
  getBoardTileAbility,
  getBoardSquareLabel,
  getBoardSquareLandingTextColor,
  getBoardSquareTextColor,
  isNegativeBoardSquare,
} from './doorBoard';
import { playSound } from '../../utils/utils';
import {
  PHASE_ANCHOR_DURATION_MS,
  PHASE_ANCHOR_TIME_SCALE,
} from './phaseAnchor';
import './PhaseAnchor.css';
import { GHOST_BUMP_LIMIT } from './ghostBump';
import './GhostBump.css';
import { applyPortalTransit, getPairedPortalIndex } from './portalPair';
import { createPortalPairScene } from './portalPairScene';
import { createBountyTagScene } from './bountyTagScene';
import { createChainPreviewScene } from './chainPreviewScene';
import { getChainReactionIndices } from './chainReaction';
import { createBoardConditionScene } from './boardConditionScene';
import { createPowerUpScene } from './powerUpScene';
import { createGambitScene } from './gambitScene';
import { createTreasureTileScene } from './treasureTileScene';
import {
  createHealingTileScene,
  getHealingTileIndex,
  HEAL_TILE_FEEDBACK_MS,
} from './healingTileScene';
import './HealingTile.css';
import { createShieldScene } from './shieldScene';
import { createTrapScene, getTrapVariant } from './trapScene';
import './Trap.css';
import { createAttackStyleAuraScene } from './attackStyleAuraScene';
import {
  getRelaunchLanding,
  boostPowerUpRoll,
  getPowerUpFaceValues,
  getPowerUpBonusPercent,
  getPowerUpMultiplier,
  upgradePowerUpTile,
  POWER_UP_CHARGE_SECONDS,
  POWER_UP_BLAST_SECONDS,
} from './powerUp';
import './PowerUp.css';
import './Shield.css';
import './AttackStyleAura.css';
import {
  applyBoardConditionStep,
  getConditionFriction,
  getConditionGravity,
  getIceTileIndices,
  getMagnetPosition,
  startConditionThrow,
} from './boardConditions';
import './BountyTag.css';
import './PortalPair.css';
import {
  getSlingshotPreview,
  getSlingshotPreviewTile,
  getSlingshotFallbackTile,
  getSlingshotVelocity,
  getSlingshotOrigin,
  applySlingshotBounds,
  getMaxSlingshotPull,
  MAX_SLINGSHOT_ANGLE,
  SLINGSHOT_ORIGIN,
} from './slingshot';
import {
  applyBoardBump,
  BOARD_BUMP_ANIMATION_TIME,
  MAX_BOARD_BUMPS,
} from './boardBump';

const CUBE_SIZE = 0.86;
const BOARD_SURFACE_Y = -1.245;
const FLOOR_Y = BOARD_SURFACE_Y + CUBE_SIZE / 2;
const BOARD_CELLS = 5;
const BOARD_SPACING = 2.85;
const BOARD_TILE_SIZE = 2.7;
const CHAIN_TILE_RADIUS = BOARD_TILE_SIZE / 2 + 0.12;
const CHAIN_VIEW_FILL = 1.5;
const BOARD_EDGE =
  (BOARD_SPACING * (BOARD_CELLS - 1)) / 2 + BOARD_TILE_SIZE / 2;
const BOARD_CAMERA_HEIGHT = 21;
const BOARD_CAMERA_DISTANCE = 14;
const SETUP_CAMERA_HEIGHT = 20;
const SETUP_CAMERA_DISTANCE = 17;
const SETUP_LOOK_Z = -0.6;
const CORE_READY_CAMERA_ELEVATION = 50;
const CORE_READY_CAMERA_DISTANCE = 24;
const BOARD_ARRIVAL_SECONDS = 1.65;
const MOBILE_ROLL_ZOOM_DURATION = 3.2;
const MAX_ROLL_DURATION = 7.5;
const PHYSICS_STEP = 1 / 120;
const CUBE_MASS = 1;
const CUBE_INV_MASS = 1 / CUBE_MASS;
const CUBE_INERTIA = (CUBE_SIZE * CUBE_SIZE) / 6;
const CUBE_INV_INERTIA = 1 / CUBE_INERTIA;
const DIE_FRICTION = 0.58;
const RESTITUTION_THRESHOLD = 0.85;
const MIN_BOUNCE_RESTITUTION = 0.18;
const MAX_BOUNCE_RESTITUTION = 0.46;
const AIR_SPIN_DAMPING = 0.14;
const ROLLING_RESISTANCE = 0.14;

const makeNumberTexture = (value, color, dieType) => {
  const canvas = document.createElement('canvas');
  canvas.width = 512;
  canvas.height = 512;
  const context = getLabelContext(canvas);
  if (!context) return null;
  const texture = createLabelTexture(canvas);
  updateLabelTexture(texture, value, color, dieType, paintDieFace);
  return texture;
};

const updateNumberTexture = (texture, value, color, dieType) => {
  updateLabelTexture(texture, value, color, dieType, paintDieFace);
};

const boardLabel = (square) => {
  if (square.effect?.type === 'corruption') return 'CORRUPTED −100';
  if (square.coreDamage) return getBoardSquareLabel(square);
  if (square.effect?.type === 'critical') return 'CRITICAL HIT';
  if (square.effect?.type === 'criticalMiss') return 'CRITICAL MISS';
  if (square.operation === 'multiply') return `×${square.value}`;
  if (square.operation === 'divide') return `÷${square.value}`;
  const modifier =
    square.modifier > 0 ? `+${square.modifier}` : `${square.modifier}`;
  if (square.effect?.type === 'relic') return 'CHEST';
  if (square.effect?.type === 'shield') return `${square.effect.amount} BLOCK`;
  if (square.effect?.type === 'massiveAttack') return 'MASSIVE ATTACK';
  if (square.effect?.type === 'heal') return `+ ${square.effect.amount} Max HP`;
  if (square.effect?.type === 'damage') return `−${square.effect.amount} HP`;
  if (square.effect?.type === 'shards')
    return `+${square.effect.amount} SHARDS`;
  if (square.effect?.type === 'damage') return `${modifier} HAZARD`;
  return modifier;
};

const makeBoardTexture = (
  square,
  landed = false,
  { compact = false, anisotropy = 1, resolution: resolutionOverride } = {}
) => {
  const canvas = document.createElement('canvas');
  const size = 256;
  const resolution = resolutionOverride || (compact ? 2 : 1);
  canvas.width = size * resolution;
  canvas.height = size * resolution;
  const context = getLabelContext(canvas);
  if (!context) return null;
  context.scale(resolution, resolution);
  const fontFamily = compact ? 'Arial, sans-serif' : 'monospace';
  const makeTexture = () => {
    const texture = createLabelTexture(canvas);
    // Keep oblique labels sharp instead of sampling a blurred mip level.
    texture.anisotropy = anisotropy;
    return texture;
  };
  const drawText = (value, x, y) => {
    if (compact) {
      context.strokeStyle = '#020a16';
      context.lineWidth = 4;
      context.lineJoin = 'round';
      context.strokeText(value, x, y);
    }
    context.fillText(value, x, y);
  };
  const impactTextColor = landed
    ? getBoardSquareLandingTextColor(square)
    : getBoardSquareTextColor(square);
  if (square.effect?.type === 'corruption') {
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillStyle = '#000000';
    context.shadowBlur = 0;
    context.font = '900 32px Arial, sans-serif';
    context.fillText('CORRUPTED', 128, 75);
    context.font = '900 66px Arial, sans-serif';
    context.fillText('−100', 128, 148);
    return makeTexture();
  }
  const ability = getBoardTileAbility(square.effect);
  if (ability) {
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillStyle = impactTextColor;
    context.shadowColor = impactTextColor;
    context.shadowBlur = compact ? 0 : 9;
    context.font = '900 112px Arial, sans-serif';
    drawCoreboundIcon(
      context,
      square.effect.type,
      impactTextColor,
      72,
      36,
      112
    );
    let labelSize = 40;
    do {
      context.font = `900 ${labelSize}px Arial, sans-serif`;
      labelSize -= 1;
    } while (labelSize > 22 && context.measureText(ability.short).width > 230);
    drawText(ability.short, 128, 193);
    return makeTexture();
  }
  if (['heal', 'shield'].includes(square.effect?.type)) {
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.shadowColor = impactTextColor;
    context.shadowBlur = compact ? 0 : 12;
    const healing = square.effect.type === 'heal';
    context.save();
    if (compact) context.translate(0, -8);
    if (healing) drawBoardHeart(context, impactTextColor);
    else drawBoardShield(context, impactTextColor);
    context.restore();
    context.fillStyle = impactTextColor;
    context.font = `900 ${compact ? 100 : 52}px ${fontFamily}`;
    drawText(
      `${healing ? '+ ' : ''}${square.effect.amount}`,
      128,
      compact ? 149 : 139
    );
    context.font = `900 ${compact ? 40 : 36}px ${fontFamily}`;
    drawText(healing ? 'Max HP' : 'BLOCK', 128, compact ? 225 : 198);
    return makeTexture();
  }
  if (square.effect?.type === 'relic') {
    context.shadowColor = '#ffd45f';
    context.shadowBlur = compact ? 0 : 20;
    context.fillStyle = '#8b5b18';
    context.strokeStyle = '#ffe28a';
    context.lineWidth = 10;
    context.beginPath();
    context.moveTo(45, 112);
    context.lineTo(65, 65);
    context.lineTo(191, 65);
    context.lineTo(211, 112);
    context.closePath();
    context.fill();
    context.stroke();
    context.fillRect(49, 112, 158, 82);
    context.strokeRect(49, 112, 158, 82);
    context.fillStyle = '#ffe28a';
    context.fillRect(119, 134, 18, 42);
    context.beginPath();
    context.arc(128, 133, 15, Math.PI, 0);
    context.stroke();
    context.shadowBlur = 0;
    return makeTexture();
  }
  if (square.effect?.type === 'shards') {
    context.fillStyle = impactTextColor;
    context.strokeStyle = impactTextColor;
    context.shadowColor = impactTextColor;
    context.shadowBlur = compact ? 0 : 14;
    context.lineWidth = 4;
    context.beginPath();
    context.moveTo(128, 22);
    context.lineTo(149, 49);
    context.lineTo(128, 78);
    context.lineTo(107, 49);
    context.closePath();
    context.stroke();
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.font = `900 ${compact ? 112 : 96}px ${fontFamily}`;
    drawText(`+${square.effect.amount}`, 128, 139);
    context.font = `900 34px ${fontFamily}`;
    drawText(square.effect.amount === 1 ? 'SHARD' : 'SHARDS', 128, 218);
    return makeTexture();
  }
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  const text = boardLabel(square);
  const words = (compact ? text.replace('CRITICAL', 'CRIT') : text).split(
    /\s+/
  );
  const maxTextWidth = size - 32;
  let fontSize = compact ? 110 : 74;
  let lines = [];
  while (fontSize >= 36) {
    context.font = `900 ${fontSize}px ${fontFamily}`;
    lines = [];
    for (const word of words) {
      const line = lines[lines.length - 1];
      const candidate = line ? `${line} ${word}` : word;
      if (line && context.measureText(candidate).width > maxTextWidth) {
        lines.push(word);
      } else if (!line && context.measureText(word).width > maxTextWidth) {
        lines.push(word);
      } else if (line) {
        lines[lines.length - 1] = candidate;
      } else {
        lines.push(word);
      }
    }
    if (
      lines.every((line) => context.measureText(line).width <= maxTextWidth) &&
      lines.length * fontSize * 1.15 <= size - 32
    )
      break;
    fontSize -= 2;
  }
  context.shadowColor = impactTextColor;
  context.shadowBlur = compact ? 0 : 18;
  context.fillStyle = impactTextColor;
  const lineHeight = fontSize * 1.15;
  const firstLineY = size / 2 - ((lines.length - 1) * lineHeight) / 2;
  lines.forEach((line, index) => {
    drawText(line, size / 2, firstLineY + index * lineHeight);
  });
  return makeTexture();
};

const addDoorBoard = (scene, board, textureOptions) => {
  const deck = new THREE.Mesh(
    new THREE.BoxGeometry(14.5, 0.32, 14.5),
    new THREE.MeshPhysicalMaterial({
      color: 0x101d35,
      emissive: 0x071b42,
      emissiveIntensity: 0.8,
      metalness: 0.86,
      roughness: 0.24,
    })
  );
  deck.position.set(0, -1.42, 0);
  deck.receiveShadow = true;
  deck.castShadow = true;
  scene.add(deck);

  const tiles = [];
  board.forEach((square, index) => {
    const row = Math.floor(index / BOARD_CELLS);
    const column = index % BOARD_CELLS;
    const isHazard = isNegativeBoardSquare(square);
    const isTreasure = square.effect?.type === 'relic';
    const isMassiveAttack = square.effect?.type === 'massiveAttack';
    const isCorrupted = square.effect?.type === 'corruption';
    const poweredUp =
      square.effect?.type === 'corePower' ||
      (square.effect?.type === 'powerUp' &&
        getPowerUpMultiplier(square.effect) > 1);
    const color = isCorrupted
      ? 0x37104f
      : isMassiveAttack
      ? 0xff2344
      : poweredUp
      ? 0x493117
      : isTreasure
      ? 0x4a3c1a
      : isHazard
      ? 0x470914
      : 0x12364b;
    const material = new THREE.MeshStandardMaterial({
      color,
      emissive: isCorrupted
        ? 0x9a28ce
        : isMassiveAttack
        ? 0xff1232
        : poweredUp
        ? 0xb98018
        : isTreasure
        ? 0x8a6412
        : isHazard
        ? 0x30070e
        : 0x075276,
      emissiveIntensity: isMassiveAttack || isCorrupted ? 0.9 : 0.42,
      metalness: 0.72,
      roughness: 0.27,
    });
    const tile = new THREE.Mesh(
      new THREE.BoxGeometry(BOARD_TILE_SIZE, 0.13, BOARD_TILE_SIZE),
      material
    );
    tile.position.set(
      (column - 2) * BOARD_SPACING,
      -1.31,
      (row - 2) * BOARD_SPACING
    );
    tile.receiveShadow = true;
    tile.castShadow = true;
    scene.add(tile);

    const texture = makeBoardTexture(square, false, textureOptions);
    const label = new THREE.Mesh(
      new THREE.PlaneGeometry(BOARD_TILE_SIZE - 0.24, BOARD_TILE_SIZE - 0.24),
      new THREE.MeshBasicMaterial({
        map: texture,
        transparent: true,
        depthWrite: false,
        side: THREE.DoubleSide,
        toneMapped: !textureOptions.compact,
      })
    );
    label.rotation.x = -Math.PI / 2;
    label.position.set(tile.position.x, -1.237, tile.position.z);
    scene.add(label);
    tiles.push({ tile, label, material, texture, square });
  });

  const borderGeometry = new THREE.EdgesGeometry(
    new THREE.BoxGeometry(14.45, 0.05, 14.45)
  );
  const border = new THREE.LineSegments(
    borderGeometry,
    new THREE.LineBasicMaterial({
      color: 0x48eaff,
      transparent: true,
      opacity: 0.7,
    })
  );
  border.position.set(0, -1.23, 0);
  scene.add(border);
  return tiles;
};

const addPlatform = (scene) => {
  const platform = new THREE.Group();
  const base = new THREE.Mesh(
    new THREE.CylinderGeometry(3.35, 3.65, 0.32, 64),
    new THREE.MeshPhysicalMaterial({
      color: 0x101d35,
      emissive: 0x071b42,
      emissiveIntensity: 0.8,
      metalness: 0.86,
      roughness: 0.24,
    })
  );
  base.position.y = -1.42;
  base.receiveShadow = true;
  base.castShadow = true;
  platform.add(base);

  const top = new THREE.Mesh(
    new THREE.CylinderGeometry(3.18, 3.18, 0.035, 64),
    new THREE.MeshStandardMaterial({
      color: 0x071224,
      emissive: 0x0b4366,
      emissiveIntensity: 0.55,
      metalness: 0.7,
      roughness: 0.32,
    })
  );
  top.position.y = -1.245;
  top.receiveShadow = true;
  platform.add(top);

  const edge = new THREE.Mesh(
    new THREE.TorusGeometry(3.12, 0.035, 10, 96),
    new THREE.MeshBasicMaterial({
      color: 0x48eaff,
      transparent: true,
      opacity: 0.8,
    })
  );
  edge.rotation.x = Math.PI / 2;
  edge.position.y = -1.2;
  platform.add(edge);
  scene.add(platform);
};

const GlitchPrismScene = ({
  phase,
  reduceMotion = !!window.matchMedia?.('(prefers-reduced-motion: reduce)')
    .matches,
  dieType,
  corePhase = 'containment',
  charge = 0,
  attackStyle = null,
  dieFaceValues = null,
  boardStatus = null,
  relaunch = null,
  shieldAmount = 0,
  trapEffect = null,
  rollLabel = '??',
  critical = false,
  flipped = false,
  rollReady = false,
  rollValue = null,
  powerUp = null,
  doorBoard = null,
  sector = 1,
  boardCondition = null,
  boardPowering = false,
  boardActiveIndex = null,
  treasureChargeIndex = null,
  boardShine = false,
  relicPowering = false,
  relicShine = false,
  concealFacesUntilSettled = false,
  allowBumps = false,
  ghostBumpArmed = false,
  onGhostBump,
  phaseAnchorArmed = false,
  onPhaseAnchor,
  portalPair = null,
  bountyTagIndex = null,
  chainReactionReady = false,
  chainReactionTiles = null,
  onBoardBump,
  onPortalTransit,
  focusSettledDie = false,
  settledLandingIndex = null,
  launch = null,
  slingshotPreview = null,
  setupDieControlRef = null,
  setupTileControlRef = null,
  preserveSetupFraming = false,
  arrival = false,
  onArrivalComplete,
  onAnimationComplete,
  onValueSettled,
  onImpact,
  encounter = null,
  coreBoss = null,
  onWebglAvailabilityChange,
}) => {
  const mountRef = useRef(null);
  const guardianReadoutRef = useRef(null);
  const boardReadoutsRef = useRef(null);
  useLayoutEffect(() => {
    const readouts = boardReadoutsRef.current;
    const stage = mountRef.current;
    if (!readouts || !stage) return undefined;
    const updateClearance = () => {
      const height = `${readouts.offsetTop + readouts.offsetHeight}px`;
      stage.style.setProperty('--corebound-board-readouts-height', height);
      stage.parentElement?.style.setProperty(
        '--corebound-board-readouts-height',
        height
      );
    };
    updateClearance();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(updateClearance);
    observer.observe(readouts);
    return () => observer.disconnect();
  }, []);
  const resizeSceneRef = useRef(null);
  const propsRef = useRef({
    sector,
    phase,
    dieType,
    encounter,
    coreBoss,
    corePhase,
    charge,
    attackStyle,
    dieFaceValues,
    relaunch,
    shieldAmount,
    trapEffect,
    rollLabel,
    critical,
    flipped,
    rollReady,
    rollValue,
    powerUp,
    doorBoard,
    boardCondition,
    boardPowering,
    boardActiveIndex,
    treasureChargeIndex,
    boardShine,
    relicPowering,
    relicShine,
    concealFacesUntilSettled,
    allowBumps,
    ghostBumpArmed,
    onGhostBump,
    phaseAnchorArmed,
    onPhaseAnchor,
    portalPair,
    bountyTagIndex,
    chainReactionReady,
    chainReactionTiles,
    onBoardBump,
    onPortalTransit,
    focusSettledDie,
    settledLandingIndex,
    launch,
    slingshotPreview,
    setupDieControlRef,
    setupTileControlRef,
    preserveSetupFraming,
    arrival,
    onArrivalComplete,
    onAnimationComplete,
    onValueSettled,
    onImpact,
  });
  const [webglAvailable, setWebglAvailable] = useState(true);
  useEffect(() => {
    onWebglAvailabilityChange?.(webglAvailable);
  }, [webglAvailable, onWebglAvailabilityChange]);
  const [physicalLanding, setPhysicalLanding] = useState(null);
  const physicalLandingRef = useRef(null);
  const relaunchId = relaunch?.id;
  const previousThrowRef = useRef({ phase, relaunchId });
  const reportPhysicalLanding = useCallback((index, requestId = null) => {
    const landing = { index, relaunchId: requestId || null };
    physicalLandingRef.current = landing;
    setPhysicalLanding(landing);
  }, []);
  useEffect(() => {
    const previous = previousThrowRef.current;
    previousThrowRef.current = { phase, relaunchId };
    if (
      phase === 'setup' ||
      (phase === 'rolling' && previous.phase !== 'rolling') ||
      (relaunchId && relaunchId !== previous.relaunchId)
    ) {
      physicalLandingRef.current = null;
      setPhysicalLanding(null);
    }
  }, [phase, relaunchId]);
  const healingTileIndex = getHealingTileIndex(
    {
      phase,
      concealFacesUntilSettled,
      relaunch,
      encounter,
      coreBoss,
      boardActiveIndex,
      settledLandingIndex,
      doorBoard,
    },
    physicalLanding
  );
  const healingAmount = doorBoard?.[healingTileIndex]?.effect?.amount || 0;
  const [healingCue, setHealingCue] = useState(null);
  const previousHealingIndex = useRef(healingTileIndex);
  useEffect(() => {
    const previousIndex = previousHealingIndex.current;
    previousHealingIndex.current = healingTileIndex;
    if (!Number.isInteger(healingTileIndex)) {
      setHealingCue(null);
      return undefined;
    }
    setHealingCue({ index: healingTileIndex, amount: healingAmount });
    if (previousIndex !== healingTileIndex)
      playSound('/sounds/rpg-bubble-power-up.mp3', { preventOverlap: true });
    const timer = setTimeout(() => setHealingCue(null), HEAL_TILE_FEEDBACK_MS);
    return () => clearTimeout(timer);
  }, [healingTileIndex, healingAmount]);
  useLayoutEffect(() => {
    // The Breach leaves its full-screen roll view when the die lands. Resize
    // and redraw before paint so its old portrait image is never squashed
    // into the smaller result panel while ResizeObserver waits for a frame.
    resizeSceneRef.current?.();
  }, [phase]);
  const combatId = encounter?.combat?.combatId;
  const combatReadyCallback = encounter?.onCombatReady;
  useEffect(() => {
    if (!webglAvailable && combatId) combatReadyCallback?.();
  }, [webglAvailable, combatId, combatReadyCallback]);
  const [relaunchStatus, setRelaunchStatus] = useState(null);
  const [shieldStatus, setShieldStatus] = useState(0);
  const fallbackRollStarted = useRef(false);
  const bumpHandlerRef = useRef(null);
  const [ghostBumpUsed, setGhostBumpUsed] = useState(false);
  const phaseAnchorHandlerRef = useRef(null);
  const [anchorStatus, setAnchorStatus] = useState({
    used: false,
    active: false,
  });
  const [bumpStatus, setBumpStatus] = useState({
    remaining: MAX_BOARD_BUMPS,
    active: false,
  });

  useEffect(() => {
    propsRef.current = {
      sector,
      phase,
      dieType,
      encounter,
      coreBoss,
      corePhase,
      charge,
      attackStyle,
      dieFaceValues,
      relaunch,
      shieldAmount,
      trapEffect,
      rollLabel,
      critical,
      flipped,
      rollReady,
      rollValue,
      powerUp,
      doorBoard,
      boardCondition,
      boardPowering,
      boardActiveIndex,
      treasureChargeIndex,
      boardShine,
      relicPowering,
      relicShine,
      concealFacesUntilSettled,
      allowBumps,
      ghostBumpArmed,
      onGhostBump,
      phaseAnchorArmed,
      onPhaseAnchor,
      portalPair,
      bountyTagIndex,
      chainReactionReady,
      chainReactionTiles,
      onBoardBump,
      onPortalTransit,
      focusSettledDie,
      settledLandingIndex,
      launch,
      slingshotPreview,
      setupDieControlRef,
      setupTileControlRef,
      preserveSetupFraming,
      arrival,
      onArrivalComplete,
      onAnimationComplete,
      onValueSettled,
      onImpact,
    };
  }, [
    sector,
    phase,
    dieType,
    encounter,
    coreBoss,
    corePhase,
    charge,
    attackStyle,
    dieFaceValues,
    relaunch,
    shieldAmount,
    trapEffect,
    rollLabel,
    critical,
    flipped,
    rollReady,
    rollValue,
    powerUp,
    doorBoard,
    boardCondition,
    boardPowering,
    boardActiveIndex,
    treasureChargeIndex,
    boardShine,
    relicPowering,
    relicShine,
    concealFacesUntilSettled,
    allowBumps,
    ghostBumpArmed,
    onGhostBump,
    phaseAnchorArmed,
    onPhaseAnchor,
    portalPair,
    bountyTagIndex,
    chainReactionReady,
    chainReactionTiles,
    onBoardBump,
    onPortalTransit,
    focusSettledDie,
    settledLandingIndex,
    launch,
    slingshotPreview,
    setupDieControlRef,
    setupTileControlRef,
    preserveSetupFraming,
    arrival,
    onArrivalComplete,
    onAnimationComplete,
    onValueSettled,
    onImpact,
  ]);

  useEffect(() => {
    if (webglAvailable || phase !== 'revealed') return;
    const value = Number(rollLabel);
    if (Number.isFinite(value)) propsRef.current.onValueSettled?.(value);
  }, [webglAvailable, phase, rollLabel]);

  useEffect(() => {
    if (webglAvailable || phase !== 'setup' || !setupTileControlRef)
      return undefined;
    const mount = mountRef.current;
    if (!mount) return undefined;
    const updateTiles = () =>
      setupTileControlRef.current?.updateFallback([
        ...mount.querySelectorAll('.coreboundSlingshotFallbackBoard > span'),
      ]);
    updateTiles();
    return observeElementSize(mount, updateTiles);
  }, [webglAvailable, phase, setupTileControlRef, doorBoard]);

  useEffect(() => {
    if (phase !== 'rolling') {
      fallbackRollStarted.current = false;
      setRelaunchStatus(null);
      return;
    }
    const current = propsRef.current;
    const fallbackKey = current.relaunch?.id || 'roll';
    if (webglAvailable || fallbackRollStarted.current === fallbackKey) return;
    fallbackRollStarted.current = fallbackKey;
    if (current.relaunch) {
      const request = current.relaunch;
      setRelaunchStatus({ ...request, stage: 'charging' });
      const reduced = window.matchMedia?.(
        '(prefers-reduced-motion: reduce)'
      ).matches;
      const timer = setTimeout(
        () => {
          setRelaunchStatus(null);
          const landingIndex = getRelaunchLanding(
            current.doorBoard,
            request.index
          );
          reportPhysicalLanding(landingIndex, request.id);
          propsRef.current.onAnimationComplete?.(
            request.after,
            landingIndex,
            request.id
          );
        },
        reduced
          ? 250
          : request.type === 'gambit'
          ? 650
          : (POWER_UP_CHARGE_SECONDS + POWER_UP_BLAST_SECONDS) * 1000
      );
      return () => clearTimeout(timer);
    }
    const finalRoll = current.concealFacesUntilSettled
      ? current.rollValue ??
        rollCoreDamage(current.coreBoss?.damageFaces || CORE_ATTACKS[0].faces)
      : Number.isInteger(current.rollValue)
      ? current.rollValue
      : rollDie(current.dieType);
    let landingIndex = Array.isArray(current.doorBoard)
      ? current.launch || current.boardCondition
        ? getSlingshotFallbackTile(
            current.launch || {
              power: current.charge,
              angle: ((Math.random() - 0.5) * Math.PI) / 2,
            },
            current.boardCondition
          )
        : Math.floor(Math.random() * current.doorBoard.length)
      : null;
    const portalDestination =
      !current.concealFacesUntilSettled && current.portalPair?.armed
        ? getPairedPortalIndex(current.portalPair, landingIndex)
        : null;
    if (portalDestination !== null) {
      landingIndex = portalDestination;
      current.onPortalTransit?.();
      playSound('/sounds/futuristic-zoom.mp3', { preventOverlap: true });
    }
    current.onImpact?.();
    reportPhysicalLanding(landingIndex);
    current.onAnimationComplete?.(finalRoll, landingIndex);
  }, [phase, webglAvailable, relaunchId, reportPhysicalLanding]);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return undefined;
    const blockedByModal = () => {
      const modal = [...document.querySelectorAll('[aria-modal="true"]')].pop();
      return modal && !modal.contains(mount);
    };
    const initialProps = propsRef.current;
    const mobileRendering = usesMobileRendering();
    const isTestRenderer =
      typeof navigator !== 'undefined' &&
      /jsdom|node\.js/i.test(navigator.userAgent);
    if (
      isTestRenderer ||
      (typeof window !== 'undefined' && !window.WebGLRenderingContext)
    ) {
      setWebglAvailable(false);
      propsRef.current.onArrivalComplete?.();
      return undefined;
    }

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(40, 1, 0.1, 100);
    const projectedDie = new THREE.Vector3();
    const setupCameraPosition = new THREE.Vector3(
      0,
      SETUP_CAMERA_HEIGHT,
      SETUP_CAMERA_DISTANCE
    );
    const setupLookTarget = new THREE.Vector3(
      0,
      BOARD_SURFACE_Y + 0.22,
      SETUP_LOOK_Z
    );
    if (initialProps.coreBoss) {
      // A third-person view behind the die, angled toward the enemy.
      // Aim at the die itself rather than using the far-board look-ahead that
      // exaggerated perspective and made the square tiles appear stretched.
      const elevation = THREE.MathUtils.degToRad(CORE_READY_CAMERA_ELEVATION);
      setupLookTarget.set(
        SLINGSHOT_ORIGIN.x,
        SLINGSHOT_ORIGIN.y,
        SLINGSHOT_ORIGIN.z
      );
      setupCameraPosition
        .copy(setupLookTarget)
        .add(
          new THREE.Vector3(
            0,
            Math.sin(elevation) * CORE_READY_CAMERA_DISTANCE,
            Math.cos(elevation) * CORE_READY_CAMERA_DISTANCE
          )
        );
    }
    if (initialProps.phase === 'setup')
      camera.position.copy(setupCameraPosition);
    else camera.position.set(0, BOARD_CAMERA_HEIGHT, BOARD_CAMERA_DISTANCE);
    const cameraLookTarget = new THREE.Vector3();
    const cameraLookPosition = new THREE.Vector3(0, FLOOR_Y - 0.4, 0);
    const chainBounds = new THREE.Box3();
    const chainCenter = new THREE.Vector3();
    const chainPoint = new THREE.Vector3();
    const fitBoardCamera = createBoardCameraFramer();
    const boardFramePoints = [];
    const addFrameBox = (x, y, z, rx, ry, rz) => {
      [-1, 1].forEach((dx) =>
        [-1, 1].forEach((dy) =>
          [-1, 1].forEach((dz) =>
            boardFramePoints.push(
              new THREE.Vector3(x + dx * rx, y + dy * ry, z + dz * rz)
            )
          )
        )
      );
    };
    addFrameBox(
      0,
      BOARD_SURFACE_Y,
      0,
      BOARD_EDGE + 0.12,
      0.25,
      BOARD_EDGE + 0.12
    );
    if (initialProps.coreBoss)
      boardFramePoints.push(...CORE_BOARD_FRAME_POINTS);
    else if (initialProps.encounter)
      addFrameBox(
        GUARDIAN_POSITION.x,
        GUARDIAN_POSITION.y,
        GUARDIAN_POSITION.z,
        3.8,
        3.8,
        1.8
      );
    const movingDiePoints = Array.from(
      { length: 8 },
      () => new THREE.Vector3()
    );
    const allFramePoints = [...boardFramePoints, ...movingDiePoints];
    const mobileViewport = window.matchMedia?.('(max-width: 850px)');
    const isCompactPreview = () =>
      mobileViewport?.matches &&
      (mobileRendering || propsRef.current.phase === 'setup');
    const setupFramingCamera = camera.clone();
    const setupFramePoint = new THREE.Vector3();
    let setupFrameKey = null;
    let setupFrameZoom = 1;
    let setupFrameOffsetY = 0;
    let setupFrameHeight = 1;
    const getSetupCameraZoom = (condition) => {
      const width = Math.max(1, mount.clientWidth);
      const height = Math.max(1, mount.clientHeight);
      const applySavedFraming = () => {
        camera.setViewOffset(
          width,
          height,
          0,
          (setupFrameOffsetY * height) / setupFrameHeight,
          width,
          height
        );
        return setupFrameZoom;
      };
      // Overview changes the canvas size, not the player's aiming camera.
      // Keep the last aiming zoom and normalized vertical framing until the
      // full-screen aiming view is restored and can fit its available space.
      if (propsRef.current.preserveSetupFraming && setupFrameKey !== null)
        return applySavedFraming();
      const wideViewport = window.innerWidth > 600;
      const mobileReady = window.innerWidth <= 850;
      const mobileCoreReady =
        !!propsRef.current.coreBoss && window.innerWidth <= 850;
      const grip = propsRef.current.setupDieControlRef?.current;
      // The input is hidden during arrival, but its responsive size must still
      // match the ready view or revealing it changes the camera's endpoint.
      const gripRadius =
        ((grip &&
          (grip.offsetHeight ||
            parseFloat(window.getComputedStyle(grip).height))) ||
          104) / 2;
      const hasEncounter = !!(
        propsRef.current.encounter || propsRef.current.coreBoss
      );
      const readoutHeight = hasEncounter
        ? boardReadoutsRef.current?.offsetHeight || 0
        : 0;
      const key = `${width}:${height}:${gripRadius}:${condition?.type}:${condition?.direction}:${hasEncounter}:${readoutHeight}:${wideViewport}:${mobileReady}:${mobileCoreReady}`;
      if (key === setupFrameKey) return applySavedFraming();
      setupFrameKey = key;
      setupFrameHeight = height;
      setupFramingCamera.clearViewOffset();
      setupFramingCamera.aspect = width / height;
      setupFramingCamera.zoom = 1;
      setupFramingCamera.position.copy(setupCameraPosition);
      setupFramingCamera.lookAt(setupLookTarget);
      setupFramingCamera.updateProjectionMatrix();
      setupFramingCamera.updateMatrixWorld(true);
      // Fit the deck, the fully stretched die, and the actual input travel,
      // then shift the projection upward so
      // the rear edge starts near the top instead of wasting that space.
      // The compact nav and horizontal vitals leave room for a tighter fit.
      // Keep a small border margin instead of enlarging the die independently.
      const extent = BOARD_EDGE + (mobileReady ? 0.18 : 0.3);
      let maxX = 0;
      let minY = Infinity;
      let maxY = -Infinity;
      const includePoint = (x, y, z, upperLimit = Infinity) => {
        setupFramePoint.set(x, y, z).project(setupFramingCamera);
        maxX = Math.max(maxX, Math.abs(setupFramePoint.x));
        minY = Math.min(minY, setupFramePoint.y);
        maxY = Math.max(maxY, Math.min(upperLimit, setupFramePoint.y));
      };
      [-extent, extent].forEach((x) => {
        [-extent, extent].forEach((z) => {
          includePoint(x, BOARD_SURFACE_Y, z);
        });
      });
      if (propsRef.current.coreBoss) {
        // On phones the board gets priority over the Core's upper silhouette.
        // Keep its center visible, allowing only the top of the model to crop.
        let coreTop = Infinity;
        if (mobileCoreReady) {
          const centerY = setupFramePoint
            .copy(CORE_BOARD_POSITION)
            .project(setupFramingCamera).y;
          const fullTop = Math.max(
            ...CORE_BOARD_FRAME_POINTS.map(
              (point) =>
                setupFramePoint.copy(point).project(setupFramingCamera).y
            )
          );
          coreTop = centerY + (fullTop - centerY) * 0.5;
        }
        CORE_BOARD_FRAME_POINTS.forEach((point) =>
          includePoint(point.x, point.y, point.z, coreTop)
        );
      } else if (hasEncounter) {
        [-3.8, 3.8].forEach((x) =>
          [-0.8, wideViewport ? 5.5 : 5.8].forEach((y) =>
            includePoint(x, y, GUARDIAN_POSITION.z)
          )
        );
      }
      [-2.5, 2.5].forEach((x) => {
        [SLINGSHOT_ORIGIN.z - 1.3, SLINGSHOT_ORIGIN.z + 2.2].forEach((z) => {
          includePoint(x, BOARD_SURFACE_Y, z);
        });
      });
      if (condition?.type === 'magnetic') {
        const magnet = getMagnetPosition(condition);
        [-0.65, 0.65].forEach((dx) =>
          [-0.65, 0.65].forEach((dz) => {
            includePoint(magnet.x + dx, BOARD_SURFACE_Y + 0.9, magnet.z + dz);
          })
        );
      } else if (condition?.type === 'lowGravity') {
        [-7.7, 7.7].forEach((x) =>
          [-7.7, 7.7].forEach((z) => {
            includePoint(x, BOARD_SURFACE_Y + 0.8, z);
          })
        );
      }
      const dieRadius = (CUBE_SIZE * Math.sqrt(3)) / 2;
      [-MAX_SLINGSHOT_ANGLE, 0, MAX_SLINGSHOT_ANGLE].forEach((angle) => {
        [-dieRadius, dieRadius].forEach((dx) => {
          [-dieRadius, dieRadius].forEach((dy) => {
            [-dieRadius, dieRadius].forEach((dz) => {
              includePoint(
                -Math.sin(angle) * 0.85 + dx,
                SLINGSHOT_ORIGIN.y + dy,
                SLINGSHOT_ORIGIN.z + Math.cos(angle) * 1.05 + dz
              );
            });
          });
        });
      });
      setupFramePoint
        .set(SLINGSHOT_ORIGIN.x, SLINGSHOT_ORIGIN.y, SLINGSHOT_ORIGIN.z)
        .project(setupFramingCamera);
      const idleDieY = setupFramePoint.y;
      // Reserve the compact health readout above the guardian, including
      // short phone viewports where a fixed inset would cover the character.
      const topInset = hasEncounter
        ? Math.min(readoutHeight + (mobileReady ? 4 : 8), height * 0.38)
        : Math.min(8, height * 0.04);
      const top = 1 - (2 * topInset) / height;
      const bottom = -1 + (2 * Math.min(10, height * 0.04)) / height;
      const side =
        1 -
        (2 *
          Math.min(
            wideViewport ? 16 : 24,
            width * (mobileReady ? 0.015 : 0.025)
          )) /
          width;
      // Pointer capture lets a pull continue over the HUD. Count that room in
      // full-screen mode rather than shrinking the board to reserve it twice.
      const extraPullSpace = mount.closest(
        '.coreboundSlingshotLauncher.is-fullscreen'
      )
        ? Math.max(0, window.innerHeight - mount.getBoundingClientRect().bottom)
        : 0;
      const pullClearance = Math.min(
        height * 0.45,
        Math.max(
          gripRadius + 12,
          getMaxSlingshotPull(width, height) + gripRadius + 12 - extraPullSpace
        )
      );
      const idleBottom = -1 + (2 * pullClearance) / height;
      setupFrameZoom = Math.min(
        side / Math.max(0.01, maxX),
        (top - bottom) / Math.max(0.01, maxY - minY),
        (top - idleBottom) / Math.max(0.01, maxY - idleDieY)
      );
      const verticalShift = top - maxY * setupFrameZoom;
      setupFrameOffsetY = (verticalShift * height) / 2;
      camera.setViewOffset(width, height, 0, setupFrameOffsetY, width, height);
      return setupFrameZoom;
    };

    let renderer;
    let frame;
    let graphicsLost = false;
    try {
      renderer = createCoreboundRenderer({
        antialias: true,
        alpha: true,
      });
    } catch {
      setWebglAvailable(false);
      propsRef.current.onArrivalComplete?.();
      return undefined;
    }
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.15;
    renderer.setClearColor(0x000000, 0);
    mount.appendChild(renderer.domElement);

    scene.add(new THREE.HemisphereLight(0x96cfff, 0x060713, 1.7));
    const keyLight = new THREE.DirectionalLight(0xfff0c2, 5.4);
    keyLight.position.set(-4, 7, 5);
    keyLight.castShadow = true;
    keyLight.shadow.mapSize.set(1024, 1024);
    keyLight.shadow.camera.left = -5;
    keyLight.shadow.camera.right = 5;
    keyLight.shadow.camera.top = 5;
    keyLight.shadow.camera.bottom = -5;
    scene.add(keyLight);
    const cyanLight = new THREE.PointLight(0x26ddff, 5.5, 9, 2);
    cyanLight.position.set(3, 2, 3);
    scene.add(cyanLight);
    const violetLight = new THREE.PointLight(0xa65cff, 3.8, 8, 2);
    violetLight.position.set(-3, 1, 0);
    scene.add(violetLight);
    const criticalLight = new THREE.PointLight(0xff66c9, 0, 10, 1.4);
    criticalLight.position.set(0, 1, 2);
    scene.add(criticalLight);
    let universe = createSectorEnvironment(
      initialProps.coreBoss ? 10 : initialProps.sector
    );
    scene.add(universe.group);
    const boardRoot = new THREE.Group();
    scene.add(boardRoot);
    const boardAnisotropy = Math.min(
      8,
      renderer.capabilities?.getMaxAnisotropy?.() || 1
    );
    let boardTextureOptions = {
      compact: !!isCompactPreview(),
      anisotropy: boardAnisotropy,
      resolution: 2,
    };
    let boardTiles = [];
    if (Array.isArray(initialProps.doorBoard)) {
      boardTiles = addDoorBoard(
        boardRoot,
        initialProps.doorBoard,
        boardTextureOptions
      );
    } else {
      addPlatform(scene);
    }
    const portals = createPortalPairScene(boardRoot, BOARD_SURFACE_Y);
    const bountyMarker = createBountyTagScene(boardRoot, BOARD_SURFACE_Y);
    const chainPreview = createChainPreviewScene(boardRoot, BOARD_SURFACE_Y);
    const environment = createBoardConditionScene(boardRoot, BOARD_SURFACE_Y);
    const powerUpEffects = createPowerUpScene(scene, BOARD_SURFACE_Y);
    const gambitEffects = createGambitScene(scene);
    const treasureTileEffects = createTreasureTileScene(
      boardRoot,
      BOARD_SURFACE_Y
    );
    const healingTileEffects = createHealingTileScene(
      boardRoot,
      BOARD_SURFACE_Y
    );
    const shieldEffects = createShieldScene(scene, BOARD_SURFACE_Y);
    const trapEffects = createTrapScene(scene);
    const styleAura = createAttackStyleAuraScene(scene);
    const guardianScene = createGuardianScene(scene);
    const coreBoardScene = createCoreBoardScene(scene);
    const guardianCounter = createGuardianCounterScene(scene);
    const guardianStrike = createGuardianStrikeScene(scene, {
      target: initialProps.coreBoss
        ? CORE_BOARD_STRIKE_TARGET
        : GUARDIAN_POSITION,
    });
    let combatKey = null;
    let coreContactNotified = null;
    let coreReturnNotified = null;
    const guardianCamera = createGuardianAttackCamera();
    const boardDieCamera = createBoardDieCamera(BOARD_SPACING);
    const combatPoint = new THREE.Vector3();
    let activeShieldAmount = 0;
    let suppressInitialShieldCue = propsRef.current.shieldAmount > 0;
    const activateShield = (amount) => {
      if (!(amount > activeShieldAmount)) return;
      const firstActivation = activeShieldAmount === 0;
      activeShieldAmount = amount;
      shieldEffects.activate();
      setShieldStatus(amount);
      if (firstActivation) {
        if (!suppressInitialShieldCue)
          playSound('/sounds/shield-charge-up.mp3');
        suppressInitialShieldCue = false;
      }
    };
    const resetShield = () => {
      activeShieldAmount = 0;
      shieldEffects.reset();
      setShieldStatus(0);
    };

    const root = new THREE.Group();
    root.position.set(0, FLOOR_Y + 0.3, 0);
    scene.add(root);

    const sling = new THREE.Group();
    scene.add(sling);
    const dock = new THREE.Mesh(
      new THREE.CylinderGeometry(2.3, 2.4, 0.2, 64),
      new THREE.MeshPhysicalMaterial({
        color: '#101c35',
        emissive: '#102b50',
        emissiveIntensity: 0.7,
        metalness: 0.85,
        roughness: 0.28,
      })
    );
    dock.scale.z = 0.68;
    dock.position.set(0, -1.42, SLINGSHOT_ORIGIN.z + 0.35);
    dock.receiveShadow = true;
    dock.visible = boardTiles.length > 0;
    scene.add(dock);
    const dockRing = new THREE.Mesh(
      new THREE.TorusGeometry(2.3, 0.018, 8, 80),
      new THREE.MeshBasicMaterial({
        color: '#66e9ff',
        transparent: true,
        opacity: 0.6,
      })
    );
    dockRing.rotation.x = -Math.PI / 2;
    dockRing.scale.y = 0.68;
    dockRing.position.set(0, -1.31, SLINGSHOT_ORIGIN.z + 0.35);
    dockRing.visible = boardTiles.length > 0;
    scene.add(dockRing);
    const tetherMaterial = new THREE.MeshBasicMaterial({
      color: 0x8ff9ff,
      transparent: true,
      opacity: 0.95,
    });
    const tetherGlowMaterial = new THREE.MeshBasicMaterial({
      color: 0x885dff,
      transparent: true,
      opacity: 0.34,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    const tetherGeometry = new THREE.CylinderGeometry(0.032, 0.032, 1, 8);
    const slingFrameMaterial = new THREE.MeshStandardMaterial({
      color: 0x151a31,
      metalness: 0.95,
      roughness: 0.18,
      emissive: 0x30206f,
      emissiveIntensity: 0.82,
    });
    const slingArmorMaterial = new THREE.MeshStandardMaterial({
      color: 0x3b4260,
      metalness: 0.94,
      roughness: 0.22,
      emissive: 0x121b42,
      emissiveIntensity: 0.7,
    });
    const slingCoreMaterial = new THREE.MeshBasicMaterial({
      color: 0x7df8ff,
      transparent: true,
      opacity: 0.92,
    });
    const slingAccentMaterial = new THREE.MeshBasicMaterial({
      color: 0xbca0ff,
      transparent: true,
      opacity: 0.88,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const tethers = [-1.4, 1.4].map((x) => {
      const postZ = SLINGSHOT_ORIGIN.z + 0.5;
      const anchor = new THREE.Vector3(x, FLOOR_Y + 0.62, postZ);
      const post = new THREE.Mesh(
        new THREE.CylinderGeometry(0.13, 0.22, 1.18, 6),
        slingFrameMaterial
      );
      const armTilt = -Math.sign(x) * 0.16;
      post.position.set(x, BOARD_SURFACE_Y + 0.58, postZ);
      post.rotation.z = armTilt;
      post.castShadow = true;
      sling.add(post);

      const armor = new THREE.Mesh(
        new THREE.BoxGeometry(0.13, 0.72, 0.2),
        slingArmorMaterial
      );
      armor.position.set(
        x - Math.sign(x) * 0.09,
        BOARD_SURFACE_Y + 0.56,
        postZ + 0.02
      );
      armor.rotation.z = armTilt;
      armor.castShadow = true;
      sling.add(armor);

      const foot = new THREE.Mesh(
        new THREE.CylinderGeometry(0.24, 0.31, 0.14, 6),
        slingFrameMaterial
      );
      foot.position.set(x, BOARD_SURFACE_Y + 0.07, postZ);
      sling.add(foot);
      const core = new THREE.Mesh(
        new THREE.CylinderGeometry(0.045, 0.055, 0.88, 8),
        slingCoreMaterial
      );
      core.position.set(x, BOARD_SURFACE_Y + 0.56, postZ + 0.12);
      core.rotation.z = armTilt;
      sling.add(core);

      const baseCoil = new THREE.Mesh(
        new THREE.TorusGeometry(0.25, 0.035, 8, 32),
        slingAccentMaterial
      );
      baseCoil.rotation.x = -Math.PI / 2;
      baseCoil.position.set(x, BOARD_SURFACE_Y + 0.18, postZ);
      sling.add(baseCoil);

      const clampRing = new THREE.Mesh(
        new THREE.TorusGeometry(0.18, 0.034, 8, 32),
        slingAccentMaterial
      );
      clampRing.rotation.x = Math.PI / 2;
      clampRing.position.set(x, anchor.y - 0.06, postZ + 0.04);
      sling.add(clampRing);
      const clamp = new THREE.Mesh(
        new THREE.SphereGeometry(0.16, 12, 8),
        slingCoreMaterial
      );
      clamp.position.copy(anchor);
      sling.add(clamp);
      const band = new THREE.Mesh(tetherGeometry, tetherMaterial);
      const glow = new THREE.Mesh(tetherGeometry, tetherGlowMaterial);
      sling.add(band, glow);
      return { anchor, band, glow };
    });
    const pathGeometry = new THREE.BufferGeometry();
    const pathPositions = new Float32Array(41 * 3);
    const pathDistances = new Float32Array(41);
    pathGeometry.setAttribute(
      'position',
      new THREE.BufferAttribute(pathPositions, 3)
    );
    pathGeometry.setAttribute(
      'lineDistance',
      new THREE.BufferAttribute(pathDistances, 1)
    );
    const path = new THREE.Line(
      pathGeometry,
      new THREE.LineDashedMaterial({
        color: 0x74ffe8,
        transparent: true,
        opacity: 0.8,
        dashSize: 0.22,
        gapSize: 0.14,
        depthWrite: false,
      })
    );
    path.frustumCulled = false;
    sling.add(path);
    const reticle = new THREE.Mesh(
      new THREE.RingGeometry(0.48, 0.55, 48),
      new THREE.MeshBasicMaterial({
        color: 0x8affef,
        transparent: true,
        opacity: 0.8,
        side: THREE.DoubleSide,
        depthWrite: false,
      })
    );
    reticle.rotation.x = -Math.PI / 2;
    sling.add(reticle);
    const reticleHalo = new THREE.Mesh(
      new THREE.RingGeometry(0.65, 0.85, 48),
      new THREE.MeshBasicMaterial({
        color: 0x83aaff,
        transparent: true,
        opacity: 0.16,
        side: THREE.DoubleSide,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      })
    );
    reticleHalo.rotation.x = -Math.PI / 2;
    sling.add(reticleHalo);
    const tetherDirection = new THREE.Vector3();
    const tetherUp = new THREE.Vector3(0, 1, 0);
    const updateTethers = (position) => {
      tethers.forEach(({ anchor, band, glow }) => {
        tetherDirection.subVectors(position, anchor);
        const length = tetherDirection.length();
        band.position.copy(anchor).addScaledVector(tetherDirection, 0.5);
        band.quaternion.setFromUnitVectors(
          tetherUp,
          tetherDirection.normalize()
        );
        band.scale.set(1, length, 1);
        glow.position.copy(band.position);
        glow.quaternion.copy(band.quaternion);
        glow.scale.set(3, length, 3);
      });
    };
    let previewSignature = '';

    const cubeSize = CUBE_SIZE;
    const cubeGeometry = new THREE.BoxGeometry(cubeSize, cubeSize, cubeSize);
    const cubeMaterial = new THREE.MeshPhysicalMaterial({
      color: 0x123d5d,
      emissive: 0x078eb4,
      emissiveIntensity: 1.05,
      metalness: 0.72,
      roughness: 0.2,
      clearcoat: 1,
      clearcoatRoughness: 0.12,
    });
    const cube = new THREE.Mesh(cubeGeometry, cubeMaterial);
    cube.castShadow = true;
    cube.receiveShadow = true;
    root.add(cube);
    const dieGlow = new THREE.PointLight(0x42e9ff, 2.6, 3.8, 2);
    dieGlow.position.set(0, 0.3, 0.42);
    root.add(dieGlow);
    const dieEdges = new THREE.LineSegments(
      new THREE.EdgesGeometry(cubeGeometry),
      new THREE.LineBasicMaterial({
        color: 0xaffbff,
        transparent: true,
        opacity: 0.95,
      })
    );
    root.add(dieEdges);
    const coreDieEffects = initialProps.concealFacesUntilSettled
      ? createCoreDieScene(scene, root, cubeSize)
      : null;

    const faceColors = [
      '#9dfaff',
      '#ff9bd5',
      '#ffe777',
      '#c797ff',
      '#79ffa8',
      '#ff9b7a',
    ];
    const numberLabels = [];
    const faceValues = Array(6).fill('--');
    const displayedFaceValues = [...faceValues];
    const half = cubeSize / 2 + 0.009;
    const addFaceNumber = (position, rotation, color) => {
      const texture = makeNumberTexture('--', color);
      const label = new THREE.Mesh(
        new THREE.PlaneGeometry(cubeSize - 0.04, cubeSize - 0.04),
        new THREE.MeshBasicMaterial({
          map: texture,
          transparent: true,
          depthWrite: false,
          side: THREE.DoubleSide,
        })
      );
      label.position.copy(position);
      label.rotation.set(rotation.x, rotation.y, rotation.z);
      root.add(label);
      const glow = new THREE.Mesh(
        new THREE.PlaneGeometry(0.67, 0.67),
        new THREE.MeshBasicMaterial({
          map: texture,
          color: 0xffffff,
          transparent: true,
          opacity: 0,
          depthWrite: false,
          depthTest: false,
          blending: THREE.AdditiveBlending,
          side: THREE.DoubleSide,
        })
      );
      glow.position.copy(position);
      glow.rotation.set(rotation.x, rotation.y, rotation.z);
      glow.visible = false;
      root.add(glow);
      numberLabels.push({
        label,
        glow,
        texture,
        color,
        defaultRotation: label.rotation.clone(),
      });
    };

    addFaceNumber(
      new THREE.Vector3(0, 0, half),
      new THREE.Euler(0, 0, 0),
      faceColors[0]
    );
    addFaceNumber(
      new THREE.Vector3(half, 0, 0),
      new THREE.Euler(0, Math.PI / 2, 0),
      faceColors[1]
    );
    addFaceNumber(
      new THREE.Vector3(0, 0, -half),
      new THREE.Euler(0, Math.PI, 0),
      faceColors[2]
    );
    addFaceNumber(
      new THREE.Vector3(-half, 0, 0),
      new THREE.Euler(0, -Math.PI / 2, 0),
      faceColors[3]
    );
    addFaceNumber(
      new THREE.Vector3(0, half, 0),
      new THREE.Euler(-Math.PI / 2, 0, 0),
      faceColors[4]
    );
    addFaceNumber(
      new THREE.Vector3(0, -half, 0),
      new THREE.Euler(Math.PI / 2, 0, 0),
      faceColors[5]
    );

    // The player's strike returns here before the Core takes its turn. Keep
    // that same die visible while the independent counter-die rolls.
    let defender = null;
    let defenderAura = null;
    const defenderTarget = new THREE.Vector3(
      SLINGSHOT_ORIGIN.x,
      SLINGSHOT_ORIGIN.y,
      SLINGSHOT_ORIGIN.z
    );
    if (initialProps.coreBoss?.playerDie) {
      const player = initialProps.coreBoss.playerDie;
      const skin = getDie(player.dieId);
      defender = new THREE.Group();
      defender.name = 'core-player-die';
      defender.position.copy(defenderTarget);
      defender.rotation.x = 0.08;
      const body = cube.clone();
      body.material = cubeMaterial.clone();
      body.material.color.set(skin.body);
      body.material.emissive.set(skin.emissive);
      body.material.metalness = skin.metalness;
      body.material.roughness = skin.roughness;
      defender.add(body);
      const edges = dieEdges.clone();
      edges.material = dieEdges.material.clone();
      edges.material.color.set(skin.color);
      defender.add(edges);
      numberLabels.forEach(({ label }, index) => {
        const face = label.clone();
        face.material = label.material.clone();
        face.material.map = makeNumberTexture(
          player.faces?.[index] ?? skin.faces?.[index] ?? '?',
          skin.color,
          skin.id
        );
        defender.add(face);
      });
      const glow = dieGlow.clone();
      glow.color.set(skin.color);
      defender.add(glow);
      scene.add(defender);
      defenderAura = createAttackStyleAuraScene(scene);
    }
    const counterCameraPosition = new THREE.Vector3();
    const counterCameraLook = new THREE.Vector3();
    const counterFramingCamera = new THREE.PerspectiveCamera();
    const counterDiePoints = Array.from(
      { length: 8 },
      () => new THREE.Vector3()
    );
    const counterHeartPoints = Array.from(
      { length: 16 },
      () => new THREE.Vector3()
    );
    const counterFramePoints = [
      ...CORE_BOARD_FRAME_POINTS,
      ...counterHeartPoints,
      ...counterDiePoints,
    ];
    let counterCameraStage = null;
    let counterCameraStageStartedAt = 0;

    const cubeAxisX = new THREE.Vector3(1, 0, 0);
    const cubeAxisY = new THREE.Vector3(0, 1, 0);
    const cubeAxisZ = new THREE.Vector3(0, 0, 1);
    const supportOffset = new THREE.Vector3();
    const contactNormal = new THREE.Vector3(0, 1, 0);
    const contactVelocity = new THREE.Vector3();
    const tangentVelocity = new THREE.Vector3();
    const tangentDirection = new THREE.Vector3();
    const contactImpulse = new THREE.Vector3();
    const angularImpulse = new THREE.Vector3();
    const rCrossDirection = new THREE.Vector3();
    const angularStepAxis = new THREE.Vector3();
    const angularStepQuaternion = new THREE.Quaternion();
    const getVerticalSupport = () => {
      cubeAxisX.set(1, 0, 0).applyQuaternion(root.quaternion);
      cubeAxisY.set(0, 1, 0).applyQuaternion(root.quaternion);
      cubeAxisZ.set(0, 0, 1).applyQuaternion(root.quaternion);
      return (
        (cubeSize / 2) *
        (Math.abs(cubeAxisX.y) + Math.abs(cubeAxisY.y) + Math.abs(cubeAxisZ.y))
      );
    };
    const getSupportOffset = () => {
      getVerticalSupport();
      supportOffset
        .set(0, 0, 0)
        .addScaledVector(cubeAxisX, -(cubeSize / 2) * Math.sign(cubeAxisX.y))
        .addScaledVector(cubeAxisY, -(cubeSize / 2) * Math.sign(cubeAxisY.y))
        .addScaledVector(cubeAxisZ, -(cubeSize / 2) * Math.sign(cubeAxisZ.y));
      return supportOffset;
    };

    const physics = {
      velocity: new THREE.Vector3(),
      angular: new THREE.Vector3(),
      restingTime: 0,
      flashTime: 0,
      active: false,
      resting: false,
      completed: false,
      numbersFrozen: false,
      facesRevealed: false,
      grounding: false,
      grounded: false,
      onGround: false,
      enteredBoard: false,
      groundElapsed: 0,
      rollElapsed: 0,
      rollDeadline: MAX_ROLL_DURATION,
      bumpsUsed: 0,
      bumpTime: 0,
      topFaceIndex: 4,
      landingIndex: null,
      lastPresentedLabel: null,
      valueTween: null,
      guardianFacesReset: false,
      lastBoardPowering: false,
      lastBoardShine: false,
      lastRelicPowering: false,
      lastRelicShine: false,
      boardPowerTime: 0,
      boardShineTime: 0,
      relicPowerTime: 0,
      relicShineTime: 0,
      targetCameraZoom: 1,
      lastPhase: null,
      impactPlayed: false,
      phaseAnchorEndsAt: 0,
      phaseAnchorUsed: false,
      portalUsed: false,
      portalFlash: 0,
      portalSnapshot: null,
      relaunchId: null,
      relaunchStage: null,
      relaunchElapsed: 0,
      relaunchRequest: null,
      relaunchFaces: null,
      relaunchOrigin: new THREE.Vector3(),
    };
    const anchorHalo = new THREE.Group();
    [0.85, 1.08].forEach((radius) => {
      const ring = new THREE.Mesh(
        new THREE.TorusGeometry(radius, 0.012, 8, 64),
        new THREE.MeshBasicMaterial({
          color: 0x9bfaf4,
          transparent: true,
          opacity: 0.65,
        })
      );
      ring.rotation.x = Math.PI / 2;
      anchorHalo.add(ring);
    });
    anchorHalo.visible = false;
    scene.add(anchorHalo);
    const requestPhaseAnchor = () => {
      const current = propsRef.current;
      if (
        !current.phaseAnchorArmed ||
        !current.allowBumps ||
        current.phase !== 'rolling' ||
        !boardTiles.length ||
        physics.phaseAnchorUsed ||
        physics.completed ||
        physics.grounded ||
        (!physics.active && !physics.grounding)
      )
        return false;
      if (current.onPhaseAnchor?.() === false) return false;
      physics.phaseAnchorUsed = true;
      physics.phaseAnchorEndsAt = performance.now() + PHASE_ANCHOR_DURATION_MS;
      setAnchorStatus({ used: true, active: true });
      playSound('/sounds/slowdown-time.mp3', {
        preventOverlap: true,
      });
      return true;
    };
    phaseAnchorHandlerRef.current = requestPhaseAnchor;
    const onAnchorKeyDown = (event) => {
      if (
        (event.code !== 'KeyA' && event.key?.toLowerCase() !== 'a') ||
        event.repeat ||
        event.defaultPrevented ||
        event.altKey ||
        event.ctrlKey ||
        event.metaKey ||
        event.target?.closest?.(
          'input, textarea, select, [contenteditable]:not([contenteditable="false"])'
        ) ||
        blockedByModal()
      )
        return;
      if (requestPhaseAnchor()) event.preventDefault();
    };
    window.addEventListener('keydown', onAnchorKeyDown);
    const bumpRaycaster = new THREE.Raycaster();
    const bumpPointer = new THREE.Vector2();
    const bumpPlane = new THREE.Plane(
      new THREE.Vector3(0, 1, 0),
      -BOARD_SURFACE_Y
    );
    const bumpTarget = new THREE.Vector3();
    const requestBump = (event) => {
      const current = propsRef.current;
      const usingGhost =
        !current.allowBumps &&
        current.concealFacesUntilSettled &&
        current.ghostBumpArmed;
      const bumpLimit = usingGhost ? GHOST_BUMP_LIMIT : MAX_BOARD_BUMPS;
      let target = null;
      if (Number.isFinite(event?.clientX) && Number.isFinite(event?.clientY)) {
        const bounds = renderer.domElement.getBoundingClientRect();
        if (bounds.width && bounds.height) {
          bumpPointer.set(
            ((event.clientX - bounds.left) / bounds.width) * 2 - 1,
            -((event.clientY - bounds.top) / bounds.height) * 2 + 1
          );
          camera.updateMatrixWorld(true);
          bumpRaycaster.setFromCamera(bumpPointer, camera);
          target = bumpRaycaster.ray.intersectPlane(bumpPlane, bumpTarget);
        }
      }
      const accepted = applyBoardBump(physics, root.position, {
        allowed:
          (current.allowBumps || usingGhost) &&
          current.phase === 'rolling' &&
          boardTiles.length > 0,
        target,
        edgeLimit: BOARD_EDGE - cubeSize / 2,
        maxBumps: bumpLimit,
      });
      if (!accepted) return false;
      setBumpStatus({
        remaining: bumpLimit - physics.bumpsUsed,
        active: true,
      });
      if (usingGhost) {
        setGhostBumpUsed(true);
        current.onGhostBump?.();
      } else current.onBoardBump?.();
      playSound(
        usingGhost
          ? '/sounds/futuristic-zoom.mp3'
          : '/sounds/dice-hits-board.mp3',
        { preventOverlap: true }
      );
      return true;
    };
    bumpHandlerRef.current = requestBump;
    const onBoardPointerDown = (event) => {
      if (event.button !== 0 || event.isPrimary === false) return;
      if (requestBump(event)) event.preventDefault();
    };
    const onBumpKeyDown = (event) => {
      if (
        (event.code !== 'Space' && event.key !== ' ') ||
        event.defaultPrevented ||
        event.altKey ||
        event.ctrlKey ||
        event.metaKey ||
        event.target?.closest?.(
          'button, a, input, textarea, select, [contenteditable]:not([contenteditable="false"])'
        ) ||
        blockedByModal()
      ) {
        return;
      }
      if (
        (!propsRef.current.allowBumps &&
          !(
            propsRef.current.concealFacesUntilSettled &&
            propsRef.current.ghostBumpArmed
          )) ||
        propsRef.current.phase !== 'rolling' ||
        !boardTiles.length
      ) {
        return;
      }
      event.preventDefault();
      if (!event.repeat) requestBump();
    };
    renderer.domElement.addEventListener('pointerdown', onBoardPointerDown);
    window.addEventListener('keydown', onBumpKeyDown);
    const reduceMotion = !!window.matchMedia?.(
      '(prefers-reduced-motion: reduce)'
    ).matches;
    let arriving =
      initialProps.arrival && initialProps.phase === 'setup' && !reduceMotion;
    const cameraHandoffRef =
      initialProps.encounter?.cameraHandoffRef ||
      initialProps.coreBoss?.cameraHandoffRef;
    const arrivalHandoff = arriving ? cameraHandoffRef?.current : null;
    const arrivalSeconds =
      arrivalHandoff && initialProps.coreBoss ? 0.45 : BOARD_ARRIVAL_SECONDS;
    if (arrivalHandoff) cameraHandoffRef.current = null;
    let arrivalStartedAt = null;
    const arrivalCameraStart = new THREE.Vector3();
    const arrivalCameraOffset = new THREE.Vector3(0, 0.95, 2.25);
    const arrivalCameraEnd = setupCameraPosition.clone();
    const arrivalLookEnd = setupLookTarget.clone();
    if (initialProps.arrival && !arriving)
      propsRef.current.onArrivalComplete?.();
    const presentFaceValue = (index, value) => {
      displayedFaceValues[index] = value;
      updateNumberTexture(
        numberLabels[index].texture,
        value,
        numberLabels[index].color,
        numberLabels[index].dieType
      );
    };
    let styleFaceTween = null;
    const setFaceValue = (index, value) => {
      faceValues[index] = value;
      const current = propsRef.current;
      const hiddenChaos =
        getDie(current.dieType).id === 'chaos' &&
        !current.concealFacesUntilSettled &&
        (current.phase === 'setup' ||
          (current.phase === 'rolling' && !physics.facesRevealed));
      presentFaceValue(index, hiddenChaos ? '?' : value);
    };
    let lastDieAppearance = null;
    const updateDieAppearance = (current) => {
      const coreDie = current.concealFacesUntilSettled;
      const die = getDie(current.dieType);
      const coreSkin = getCoreDieAppearance(current.corePhase);
      const appearance = coreDie
        ? coreSkin.dieType
        : `${die.id}:${current.dieFaceValues?.join(',') || ''}`;
      if (lastDieAppearance === appearance) return;
      const preserveFaces = coreDie && lastDieAppearance?.startsWith('core:');
      const previousFaces = [...displayedFaceValues].map(Number);
      const animateStyle =
        !reduceMotion &&
        die.id !== 'chaos' &&
        lastDieAppearance &&
        current.phase === 'setup' &&
        current.dieFaceValues &&
        previousFaces.every(Number.isFinite);
      lastDieAppearance = appearance;
      cubeMaterial.color.set(coreDie ? coreSkin.body : die.body);
      cubeMaterial.emissive.set(coreDie ? coreSkin.emissive : die.emissive);
      cubeMaterial.metalness = coreDie ? coreSkin.metalness : die.metalness;
      cubeMaterial.roughness = coreDie ? coreSkin.roughness : die.roughness;
      dieGlow.color.set(coreDie ? coreSkin.light : die.color);
      dieEdges.material.color.set(coreDie ? coreSkin.accent : die.color);
      const poweredFaces =
        !coreDie && current.powerUp
          ? getPowerUpFaceValues(
              current.dieFaceValues ||
                die.faces ||
                Array.from({ length: 6 }, () => rollDie(die.id)),
              current.powerUp,
              // Fixed profiles can also repair saves made before face
              // duplication was fixed. Chaos keeps its actual random faces.
              { useSavedFaces: !!current.dieFaceValues || !die.faces }
            )
          : null;
      numberLabels.forEach((face, index) => {
        const previousDisplay = displayedFaceValues[index];
        face.color = coreDie ? coreSkin.color : die.color;
        face.dieType = coreDie ? coreSkin.dieType : die.id;
        setFaceValue(
          index,
          preserveFaces
            ? faceValues[index]
            : poweredFaces
            ? formatDieValue(poweredFaces[index])
            : (current.dieFaceValues || die.faces) && !coreDie
            ? formatDieValue((current.dieFaceValues || die.faces)[index])
            : current.phase === 'setup' && !coreDie
            ? '?'
            : '--'
        );
        if (preserveFaces) presentFaceValue(index, previousDisplay);
      });
      if (animateStyle)
        styleFaceTween = {
          from: previousFaces,
          to: faceValues.map(Number),
          elapsed: 0,
        };
    };
    const faceNormals = [
      new THREE.Vector3(0, 0, 1),
      new THREE.Vector3(1, 0, 0),
      new THREE.Vector3(0, 0, -1),
      new THREE.Vector3(-1, 0, 0),
      new THREE.Vector3(0, 1, 0),
      new THREE.Vector3(0, -1, 0),
    ];
    const groundStartQuaternion = new THREE.Quaternion();
    const groundTargetQuaternion = new THREE.Quaternion();
    const alignmentQuaternion = new THREE.Quaternion();
    const worldNormal = new THREE.Vector3();
    const worldUp = new THREE.Vector3(0, 1, 0);
    const orientLabelToCamera = (label) => {
      const labelWorldQuaternion = new THREE.Quaternion();
      const labelUp = new THREE.Vector3(0, 1, 0);
      const labelNormal = new THREE.Vector3(0, 0, 1);
      const cameraUp = new THREE.Vector3(0, 1, 0);
      label.getWorldQuaternion(labelWorldQuaternion);
      labelNormal.applyQuaternion(labelWorldQuaternion);
      labelUp.applyQuaternion(labelWorldQuaternion).projectOnPlane(labelNormal);
      cameraUp.applyQuaternion(camera.quaternion).projectOnPlane(labelNormal);
      if (labelUp.normalize().dot(cameraUp.normalize()) < 0) {
        label.rotateZ(Math.PI);
      }
    };
    const setGroundTarget = (preferredRoll = null) => {
      physics.topFaceIndex = faceNormals.reduce(
        (highestIndex, normal, index) => {
          worldNormal.copy(normal).applyQuaternion(root.quaternion);
          const highestNormal = faceNormals[highestIndex]
            .clone()
            .applyQuaternion(root.quaternion);
          return worldNormal.y > highestNormal.y ? index : highestIndex;
        },
        0
      );
      if (
        !propsRef.current.concealFacesUntilSettled &&
        Number.isInteger(preferredRoll) &&
        Number(faceValues[physics.topFaceIndex]) !== preferredRoll
      ) {
        const rolledFace = faceValues.findIndex(
          (value) => Number(value) === preferredRoll
        );
        if (rolledFace !== -1) physics.topFaceIndex = rolledFace;
      }
      worldNormal
        .copy(faceNormals[physics.topFaceIndex])
        .applyQuaternion(root.quaternion);
      alignmentQuaternion.setFromUnitVectors(worldNormal, worldUp);
      groundTargetQuaternion
        .copy(alignmentQuaternion)
        .multiply(root.quaternion)
        .normalize();
    };
    const beginGrounding = (recordedRoll = null) => {
      physics.active = false;
      physics.grounding = true;
      physics.restingTime = 0;
      physics.groundElapsed = 0;
      groundStartQuaternion.copy(root.quaternion);
      // Keep the face selected by physics. A preferred face is only needed
      // when reconstructing a saved landing without a simulated throw.
      setGroundTarget(recordedRoll);
    };
    const applyContactImpulse = (impulse, offset) => {
      physics.velocity.addScaledVector(impulse, CUBE_INV_MASS);
      angularImpulse
        .crossVectors(offset, impulse)
        .multiplyScalar(CUBE_INV_INERTIA);
      physics.angular.add(angularImpulse);
    };
    const resolveGroundContact = (step) => {
      const offset = getSupportOffset();
      contactVelocity
        .crossVectors(physics.angular, offset)
        .add(physics.velocity);

      if (contactVelocity.y >= 0) return 0;

      const impactSpeed = -contactVelocity.y;
      if (!physics.impactPlayed) {
        physics.impactPlayed = true;
        propsRef.current.onImpact?.();
      }
      const restitution =
        impactSpeed < RESTITUTION_THRESHOLD
          ? 0
          : THREE.MathUtils.clamp(
              MIN_BOUNCE_RESTITUTION + impactSpeed * 0.028,
              MIN_BOUNCE_RESTITUTION,
              MAX_BOUNCE_RESTITUTION
            );
      rCrossDirection.crossVectors(offset, contactNormal);
      const normalMass =
        CUBE_INV_MASS + rCrossDirection.lengthSq() * CUBE_INV_INERTIA;
      const normalImpulse =
        (-(1 + restitution) * contactVelocity.y) / normalMass;
      contactImpulse.copy(contactNormal).multiplyScalar(normalImpulse);
      applyContactImpulse(contactImpulse, offset);

      contactVelocity
        .crossVectors(physics.angular, offset)
        .add(physics.velocity);
      tangentVelocity.copy(contactVelocity).setY(0);
      const tangentSpeed = tangentVelocity.length();
      const surfaceFriction = getConditionFriction(
        physics.boardCondition,
        root.position
      );
      if (tangentSpeed > 0.0001) {
        tangentDirection.copy(tangentVelocity).multiplyScalar(1 / tangentSpeed);
        rCrossDirection.crossVectors(offset, tangentDirection);
        const tangentMass =
          CUBE_INV_MASS + rCrossDirection.lengthSq() * CUBE_INV_INERTIA;
        const frictionImpulse = THREE.MathUtils.clamp(
          -tangentSpeed / tangentMass,
          -DIE_FRICTION * surfaceFriction * normalImpulse,
          DIE_FRICTION * surfaceFriction * normalImpulse
        );
        contactImpulse.copy(tangentDirection).multiplyScalar(frictionImpulse);
        applyContactImpulse(contactImpulse, offset);
      }

      const normalForce = normalImpulse / step;
      physics.angular.multiplyScalar(
        Math.exp(-ROLLING_RESISTANCE * surfaceFriction * normalForce * step)
      );
      return normalImpulse;
    };
    let nextNumberSwap = 0;
    const clock = new THREE.Clock();
    let drawingBufferSize = '';
    const resize = () => {
      if (graphicsLost) return;
      const width = Math.max(1, mount.clientWidth);
      const height = Math.max(1, mount.clientHeight);
      const compact = !!isCompactPreview();
      // Retain Retina detail while bounding memory in every combat phase,
      // including full-screen incoming-hit closeups on high-DPI displays.
      const pixelRatio = getBoardPixelRatio(
        width,
        height,
        window.devicePixelRatio
      );
      const sizeKey = `${width}:${height}:${pixelRatio}`;
      if (drawingBufferSize !== sizeKey) {
        drawingBufferSize = sizeKey;
        // setPixelRatio followed by setSize clears the canvas twice, first at
        // the old dimensions. Update the drawing buffer once, only as needed.
        renderer.setDrawingBufferSize(width, height, pixelRatio);
      }
      if (compact !== boardTextureOptions.compact) {
        boardTextureOptions = { ...boardTextureOptions, compact };
        boardTiles.forEach((tile) => {
          const texture = makeBoardTexture(
            tile.square,
            false,
            boardTextureOptions
          );
          if (tile.texture) disposeCanvasTexture(tile.texture);
          tile.texture = texture;
          tile.label.material.map = texture;
          tile.label.material.toneMapped = !compact;
          tile.label.material.needsUpdate = true;
        });
      }
      camera.aspect = width / height;
      if (propsRef.current.phase === 'setup')
        camera.zoom = getSetupCameraZoom(propsRef.current.boardCondition);
      camera.updateProjectionMatrix();
      // An observer callback can run after this frame's animation render.
      // Redraw now so resizing never leaves a cleared canvas until next frame.
      renderer.render(scene, camera);
    };
    const stopObservingSize = observeElementSize(mount, resize);
    resizeSceneRef.current = resize;

    const finishPhysics = () => {
      if (physics.completed) return;
      physics.completed = true;
      physics.phaseAnchorEndsAt = 0;
      setAnchorStatus((status) => ({ ...status, active: false }));
      physics.active = false;
      physics.grounding = false;
      physics.grounded = true;
      physics.resting = true;
      physics.flashTime = 0;
      setBumpStatus((status) => ({ ...status, active: false }));
      const current = propsRef.current;
      let landingIndex = null;
      if (boardTiles.length) {
        const column = THREE.MathUtils.clamp(
          Math.round(root.position.x / BOARD_SPACING) + 2,
          0,
          BOARD_CELLS - 1
        );
        const row = THREE.MathUtils.clamp(
          Math.round(root.position.z / BOARD_SPACING) + 2,
          0,
          BOARD_CELLS - 1
        );
        landingIndex = row * BOARD_CELLS + column;
        const landed = boardTiles[landingIndex];
        const landedSquare = landed.square;
        if (landedSquare.effect?.type === 'shield') {
          activateShield(landedSquare.effect.amount);
        }
        if (landedSquare.effect?.type === 'powerUp') {
          landed.material.color.set(0xffe079);
          landed.material.emissive.set(0xb98018);
          landed.material.emissiveIntensity = 1.2;
        } else if (landedSquare.effect?.type === 'critical') {
          landed.material.color.set(0xf9e6f5);
          landed.material.emissive.set(0x7a286d);
          landed.material.emissiveIntensity = 1.8;
        } else if (
          landedSquare.effect?.type === 'criticalMiss' ||
          landedSquare.operation === 'divide'
        ) {
          landed.material.color.set(0xf3f5fa);
          landed.material.emissive.set(0x343b49);
          landed.material.emissiveIntensity = 0.65;
        } else if (landedSquare.effect?.type === 'massiveAttack') {
          landed.material.color.set(0xff2344);
          landed.material.emissive.set(0xff1232);
          landed.material.emissiveIntensity = 1.8;
        } else if (isNegativeBoardSquare(landedSquare)) {
          landed.material.color.set(0x470914);
          landed.material.emissive.set(getBoardSquareTextColor(landedSquare));
          landed.material.emissiveIntensity = 2.6;
        } else {
          landed.material.emissive.set(getBoardSquareTextColor(landedSquare));
          landed.material.emissiveIntensity = 2.6;
        }
        const landingTexture = makeBoardTexture(
          landedSquare,
          true,
          boardTextureOptions
        );
        if (landingTexture) {
          landed.label.material.map = landingTexture;
          landed.label.material.needsUpdate = true;
          if (landed.texture) disposeCanvasTexture(landed.texture);
          landed.texture = landingTexture;
        }
        landed.tile.scale.set(1.08, 1, 1.08);
        landed.label.scale.set(1.12, 1.12, 1);
      }
      const recordedRoll =
        current.phase === 'revealed' && Number.isInteger(current.rollValue)
          ? current.rollValue
          : null;
      let finalRoll;
      if (current.concealFacesUntilSettled) {
        // Faces are revealed on the first board impact, then remain fixed
        // while the die bounces and settles.
        if (!physics.facesRevealed) {
          numberLabels.forEach((_, index) => {
            setFaceValue(
              index,
              formatDieValue(
                current.coreBoss?.damageFaces?.[index] ??
                  rollCoreDamage(CORE_ATTACKS[0].faces)
              )
            );
          });
          physics.facesRevealed = true;
        }
        finalRoll = recordedRoll ?? Number(faceValues[physics.topFaceIndex]);
      } else {
        // A recorded/reduced-motion landing is also board contact.
        physics.facesRevealed = true;
        numberLabels.forEach((_, index) =>
          presentFaceValue(index, faceValues[index])
        );
        const topFaceValue = Number(faceValues[physics.topFaceIndex]);
        finalRoll =
          recordedRoll ??
          (Number.isInteger(topFaceValue) && topFaceValue >= 0
            ? topFaceValue
            : Number.isInteger(current.rollValue)
            ? current.rollValue
            : rollDie(current.dieType));
        if (faceValues[physics.topFaceIndex] === '--') {
          setFaceValue(physics.topFaceIndex, formatDieValue(finalRoll));
        }
        // Restored Chaos rolls need side values, since only the roll is saved.
        // Faces from a physical throw keep the numbers they landed with.
        numberLabels.forEach((_, index) => {
          if (faceValues[index] === '--')
            setFaceValue(index, formatDieValue(rollDie(current.dieType)));
        });
      }
      // Older random-die saves may not contain the original face values.
      if (
        recordedRoll !== null &&
        Number(faceValues[physics.topFaceIndex]) !== finalRoll
      )
        setFaceValue(physics.topFaceIndex, formatDieValue(finalRoll));
      const topLabel = numberLabels[physics.topFaceIndex];
      topLabel.glow.visible = true;
      topLabel.glow.material.opacity = 1;
      camera.position.set(
        root.position.x,
        BOARD_CAMERA_HEIGHT + root.position.y,
        BOARD_CAMERA_DISTANCE + root.position.z
      );
      camera.lookAt(root.position.x, root.position.y - 0.4, root.position.z);
      physics.targetCameraZoom = 2.35;
      camera.updateMatrixWorld(true);
      root.updateMatrixWorld(true);
      orientLabelToCamera(topLabel.label);
      topLabel.glow.rotation.copy(topLabel.label.rotation);
      physics.relaunchStage = null;
      setRelaunchStatus(
        current.relaunch ? { ...current.relaunch, stage: 'landed' } : null
      );
      reportPhysicalLanding(landingIndex, current.relaunch?.id);
      current.onAnimationComplete?.(
        finalRoll,
        landingIndex,
        current.relaunch?.id,
        faceValues.map(Number)
      );
    };

    const animate = () => {
      if (graphicsLost) return;
      frame = requestAnimationFrame(animate);
      const delta = Math.min(0.045, clock.getDelta());
      if (shouldPauseBoardRendering(mount, mobileRendering)) return;
      const current = propsRef.current;
      const destination = current.coreBoss ? 10 : current.sector || 1;
      if (universe.sector !== destination) {
        scene.remove(universe.group);
        universe.dispose();
        universe = createSectorEnvironment(destination);
        scene.add(universe.group);
      }
      universe.update(clock.elapsedTime, reduceMotion);
      const counterView =
        !!defender &&
        ['bossCharge', 'bossFire', 'bossImpact', 'bossOutcome'].includes(
          current.coreBoss?.stage
        );
      if (
        current.phase !== 'setup' &&
        !current.encounter?.combat &&
        !current.coreBoss?.combat &&
        !counterView &&
        camera.view?.enabled
      ) {
        camera.clearViewOffset();
        setupFrameKey = null;
      }
      updateDieAppearance(current);
      if (styleFaceTween && current.phase === 'setup') {
        styleFaceTween.elapsed += delta;
        const progress = Math.min(1, styleFaceTween.elapsed / 0.6);
        const eased = progress * progress * (3 - 2 * progress);
        styleFaceTween.to.forEach((value, index) =>
          presentFaceValue(
            index,
            Math.round(
              THREE.MathUtils.lerp(styleFaceTween.from[index], value, eased)
            )
          )
        );
        if (progress === 1) styleFaceTween = null;
      } else if (current.phase !== 'setup') styleFaceTween = null;
      const chargeFactor = THREE.MathUtils.clamp(current.charge, 0, 1);
      const aiming = current.phase === 'setup' && !!current.slingshotPreview;
      const anchorActive =
        current.phase === 'rolling' &&
        !physics.completed &&
        performance.now() < physics.phaseAnchorEndsAt;
      if (physics.phaseAnchorEndsAt && !anchorActive) {
        physics.phaseAnchorEndsAt = 0;
        setAnchorStatus((status) => ({ ...status, active: false }));
      }
      // Slow the simulation rather than changing momentum. Camera movement
      // and input stay responsive, and bumps still apply their usual impulse.
      const rollDelta = delta * (anchorActive ? PHASE_ANCHOR_TIME_SCALE : 1);
      anchorHalo.visible = anchorActive;
      if (anchorActive) {
        anchorHalo.position.set(
          root.position.x,
          BOARD_SURFACE_Y + 0.045,
          root.position.z
        );
        anchorHalo.scale.setScalar(
          reduceMotion ? 1 : 1 + Math.sin(clock.elapsedTime * 5) * 0.12
        );
      }

      if (physics.lastPhase !== current.phase) {
        physics.lastPhase = current.phase;
        if (
          current.phase === 'rolling' &&
          !current.relaunch &&
          !(physics.completed && current.powerUp)
        ) {
          resetShield();
          physics.relaunchId = null;
          physics.relaunchStage = null;
          physics.impactPlayed = false;
          physics.phaseAnchorEndsAt = 0;
          physics.phaseAnchorUsed = false;
          physics.portalUsed = false;
          physics.portalFlash = 0;
          physics.portalSnapshot = null;
          setAnchorStatus({ used: false, active: false });
          physics.active = true;
          physics.grounding = false;
          physics.grounded = false;
          physics.resting = false;
          physics.completed = false;
          physics.numbersFrozen =
            !!current.powerUp && !current.concealFacesUntilSettled;
          physics.facesRevealed = false;
          physics.restingTime = 0;
          physics.groundElapsed = 0;
          physics.rollElapsed = 0;
          boardDieCamera.reset();
          physics.enteredBoard = false;
          startConditionThrow(physics, current.boardCondition);
          physics.rollDeadline = MAX_ROLL_DURATION;
          physics.bumpsUsed = 0;
          setGhostBumpUsed(false);
          physics.bumpTime = 0;
          setBumpStatus({
            remaining:
              !current.allowBumps &&
              current.concealFacesUntilSettled &&
              current.ghostBumpArmed
                ? GHOST_BUMP_LIMIT
                : MAX_BOARD_BUMPS,
            active:
              (current.allowBumps ||
                (current.concealFacesUntilSettled && current.ghostBumpArmed)) &&
              boardTiles.length > 0,
          });
          physics.lastPresentedLabel = null;
          physics.guardianFacesReset = false;
          physics.boardPowerTime = 0;
          physics.targetCameraZoom =
            mobileViewport?.matches && boardTiles.length
              ? Math.min(1, Math.max(0.42, camera.aspect * 0.92))
              : 1;
          camera.zoom = physics.targetCameraZoom;
          camera.updateProjectionMatrix();
          const forwardForce = 6 + chargeFactor * 8;
          const aim =
            current.launch?.angle ?? (Math.random() - 0.5) * (Math.PI / 2);
          const velocity = current.launch
            ? getSlingshotVelocity(current.launch)
            : {
                x: Math.sin(aim) * forwardForce,
                y: forwardForce * 0.32,
                z: -Math.cos(aim) * forwardForce,
              };
          physics.velocity.set(velocity.x, velocity.y, velocity.z);
          const spin = (3 + chargeFactor * 14) * 2.15;
          if (current.launch)
            physics.angular.set(
              -Math.cos(aim) * spin,
              (Math.random() - 0.5) * spin * 0.25,
              -Math.sin(aim) * spin
            );
          else
            physics.angular.set(
              (Math.random() - 0.5) * spin * 3,
              (Math.random() - 0.5) * spin * 3,
              (Math.random() - 0.5) * spin * 3
            );
          if (current.launch)
            root.position.copy(getSlingshotOrigin(current.launch));
          else if (current.concealFacesUntilSettled && current.coreBoss) {
            root.position.copy(CORE_BOARD_STRIKE_TARGET);
            root.position.z += 0.8;
            physics.velocity.set(
              (Math.random() - 0.5) * 4,
              2.5,
              10 + Math.random() * 3
            );
            camera.position
              .copy(root.position)
              .add(
                new THREE.Vector3(0, BOARD_CAMERA_HEIGHT, BOARD_CAMERA_DISTANCE)
              );
            cameraLookPosition.copy(root.position);
            camera.lookAt(cameraLookPosition);
          } else root.position.set(0, FLOOR_Y + 1.15, SLINGSHOT_ORIGIN.z);
          root.quaternion.identity();
          root.scale.setScalar(1);
          numberLabels.forEach(({ label }) => {
            label.visible = true;
          });
          numberLabels.forEach(({ glow }) => {
            glow.visible = false;
            glow.material.opacity = 0;
          });
          const rollFaces = getPowerUpFaceValues(
            current.dieFaceValues ||
              getDie(current.dieType).faces ||
              Array.from({ length: 6 }, () => rollDie(current.dieType)),
            current.powerUp,
            {
              useSavedFaces:
                !!current.dieFaceValues || !getDie(current.dieType).faces,
            }
          );
          numberLabels.forEach((_, index) => {
            setFaceValue(
              index,
              current.concealFacesUntilSettled
                ? '?'
                : formatDieValue(rollFaces[index])
            );
          });
        } else if (current.phase === 'revealed') {
          // Reconstruct a recorded landing, including a slow-renderer fallback.
          // This also keeps a restored roll centered on the die's actual tile.
          if (
            !physics.completed &&
            (current.focusSettledDie ||
              Number.isInteger(current.settledLandingIndex))
          ) {
            if (
              Number.isInteger(current.settledLandingIndex) &&
              current.settledLandingIndex >= 0 &&
              current.settledLandingIndex < boardTiles.length
            ) {
              root.position.x =
                ((current.settledLandingIndex % BOARD_CELLS) - 2) *
                BOARD_SPACING;
              root.position.z =
                (Math.floor(current.settledLandingIndex / BOARD_CELLS) - 2) *
                BOARD_SPACING;
            }
            beginGrounding(current.rollValue);
            root.quaternion.copy(groundTargetQuaternion);
            root.position.y = BOARD_SURFACE_Y + getVerticalSupport();
            finishPhysics();
          }
          numberLabels.forEach(({ label, glow }, index) => {
            label.visible = true;
            glow.visible = index === physics.topFaceIndex;
          });
          physics.valueTween = null;
        }
      }

      if (current.relaunch && physics.relaunchId !== current.relaunch.id) {
        resetShield();
        const request = current.relaunch;
        const continuingLanding = physics.completed;
        physics.relaunchId = request.id;
        physics.relaunchRequest = request;
        physics.relaunchStage = 'charging';
        physics.relaunchElapsed = 0;
        physics.relaunchOrigin.set(
          ((request.index % BOARD_CELLS) - 2) * BOARD_SPACING,
          BOARD_SURFACE_Y,
          (Math.floor(request.index / BOARD_CELLS) - 2) * BOARD_SPACING
        );
        if (!continuingLanding)
          root.position.set(
            physics.relaunchOrigin.x,
            FLOOR_Y,
            physics.relaunchOrigin.z
          );
        physics.active = false;
        physics.resting = false;
        physics.completed = false;
        physics.grounding = false;
        physics.grounded = false;
        physics.phaseAnchorEndsAt = 0;
        physics.valueTween = null;
        physics.guardianFacesReset = false;
        physics.lastPresentedLabel = null;
        // Retain each face's own value. A charge strengthens the whole die
        // without copying the rolled number onto the other five faces.
        physics.relaunchFaces =
          request.type === 'powerUp'
            ? faceValues.map((value, index) => {
                const number = Number(value);
                return Number.isFinite(number) && number >= 0
                  ? number
                  : current.dieFaceValues?.[index] ??
                      getDie(current.dieType).faces?.[index] ??
                      rollDie(current.dieType);
              })
            : getPowerUpFaceValues(
                getDie(current.dieType).faces
                  ? current.dieFaceValues || getDie(current.dieType).faces
                  : Array.from({ length: 6 }, () =>
                      applyAttackStyle(
                        rollDie(current.dieType),
                        current.attackStyle
                      )
                    ),
                current.powerUp,
                { useSavedFaces: false }
              );
        const chargingRoll =
          request.type === 'powerUp' ? request.before : request.after;
        if (!physics.relaunchFaces.includes(chargingRoll))
          physics.relaunchFaces[physics.topFaceIndex] = chargingRoll;
        numberLabels.forEach(({ label, glow }, index) => {
          label.visible = true;
          label.material.opacity = 1;
          glow.visible = request.type === 'powerUp';
          setFaceValue(index, formatDieValue(physics.relaunchFaces[index]));
        });
        if (!continuingLanding) {
          setGroundTarget(chargingRoll);
          root.quaternion.copy(groundTargetQuaternion);
        }
        setRelaunchStatus({ ...request, stage: 'charging' });
        setBumpStatus((status) => ({ ...status, active: false }));
        if (request.type === 'powerUp')
          playSound('/sounds/dice-increase-power.mp3');
      }

      if (
        current.phase === 'revealed' &&
        !physics.guardianFacesReset &&
        current.rollLabel !== physics.lastPresentedLabel
      ) {
        physics.lastPresentedLabel = current.rollLabel;
        const topLabel = numberLabels[physics.topFaceIndex];
        const nextValue = Number(current.rollLabel);
        const previousValue = Number(displayedFaceValues[physics.topFaceIndex]);
        if (
          !reduceMotion &&
          (!current.coreBoss ||
            [
              'playerBoard',
              'playerCalculation',
              'bossBoard',
              'bossCalculation',
            ].includes(current.coreBoss.stage)) &&
          Number.isFinite(nextValue) &&
          Number.isFinite(previousValue) &&
          nextValue !== previousValue
        ) {
          physics.valueTween = {
            from: previousValue,
            to: nextValue,
            elapsed: 0,
            shown: previousValue,
          };
        } else {
          presentFaceValue(physics.topFaceIndex, current.rollLabel);
          physics.valueTween = null;
          if (Number.isFinite(nextValue)) current.onValueSettled?.(nextValue);
        }
        topLabel.glow.visible = true;
      }
      if (physics.valueTween) {
        const tween = physics.valueTween;
        tween.elapsed = Math.min(0.55, tween.elapsed + delta);
        const progress = tween.elapsed / 0.55;
        const eased = progress * progress * (3 - 2 * progress);
        const value = Math.round(tween.from + (tween.to - tween.from) * eased);
        if (value !== tween.shown || progress >= 1) {
          if (value !== tween.shown) {
            playSound(
              value > tween.shown
                ? '/sounds/dice-increase-power.mp3'
                : '/sounds/dice-decrease-power.mp3',
              { preventOverlap: true }
            );
          }
          tween.shown = value;
          // Tile/relic calculations change only the displayed top number.
          // A later relaunch still boosts the six underlying die faces.
          presentFaceValue(physics.topFaceIndex, formatDieValue(value));
        }
        if (progress >= 1) {
          physics.valueTween = null;
          current.onValueSettled?.(tween.to);
        }
      }
      if (
        Number.isInteger(current.boardActiveIndex) &&
        current.boardActiveIndex !== physics.lastBoardActiveIndex
      ) {
        physics.lastBoardActiveIndex = current.boardActiveIndex;
        const tile = boardTiles[current.boardActiveIndex];
        if (tile) {
          tile.material.emissive.set(0xc674ff);
          tile.material.emissiveIntensity = 2.8;
          tile.tile.scale.set(1.08, 1, 1.08);
          tile.label.scale.set(1.12, 1.12, 1);
        }
      }
      if (current.boardPowering && !physics.lastBoardPowering) {
        physics.boardPowerTime = 0.72;
      }
      physics.lastBoardPowering = current.boardPowering;
      if (current.boardShine && !physics.lastBoardShine) {
        physics.boardShineTime = 0.7;
      }
      physics.lastBoardShine = current.boardShine;
      if (current.relicPowering && !physics.lastRelicPowering) {
        physics.relicPowerTime = 1.05;
      }
      physics.lastRelicPowering = current.relicPowering;
      if (current.relicShine && !physics.lastRelicShine) {
        physics.relicShineTime = 0.7;
      }
      physics.lastRelicShine = current.relicShine;

      if (['charging', 'blast'].includes(physics.relaunchStage)) {
        physics.relaunchElapsed += delta;
        const request = physics.relaunchRequest;
        const isPowerUp = request.type === 'powerUp';
        const charging = physics.relaunchStage === 'charging';
        const chargeDuration = isPowerUp
          ? reduceMotion
            ? 0.25
            : POWER_UP_CHARGE_SECONDS
          : reduceMotion
          ? 0.08
          : 0.34;
        const blastDuration = isPowerUp
          ? reduceMotion
            ? 0.12
            : POWER_UP_BLAST_SECONDS
          : reduceMotion
          ? 0.05
          : 0.2;
        const duration = charging ? chargeDuration : blastDuration;
        const progress = Math.min(1, physics.relaunchElapsed / duration);
        root.position.y =
          FLOOR_Y +
          (charging
            ? progress * (isPowerUp ? 0.85 : 0.24)
            : isPowerUp
            ? 0.85
            : 0.24);
        root.scale.setScalar(
          !isPowerUp && !charging
            ? Math.max(0.04, 1 - progress)
            : reduceMotion
            ? 1
            : 1 +
              Math.sin(clock.elapsedTime * (isPowerUp ? 22 : 38)) *
                progress *
                0.035
        );
        if (isPowerUp) {
          numberLabels.forEach(({ glow }, index) => {
            const before = physics.relaunchFaces[index];
            const after = boostPowerUpRoll(before, request.multiplier);
            const value = charging
              ? Math.round(before + (after - before) * progress)
              : after;
            if (Number(faceValues[index]) !== value)
              setFaceValue(index, formatDieValue(value));
            glow.visible = true;
            glow.material.opacity = charging ? 0.35 + progress * 0.65 : 1;
          });
        }
        if (progress === 1 && charging) {
          physics.relaunchStage = 'blast';
          physics.relaunchElapsed = 0;
          if (isPowerUp) {
            const destroyed = boardTiles[request.index];
            if (destroyed) {
              destroyed.tile.scale.y = 0.16;
              destroyed.tile.position.y = -1.38;
              destroyed.label.visible = false;
              destroyed.material.color.set(0x171b26);
              destroyed.material.emissive.set(0x342312);
              destroyed.material.emissiveIntensity = 0.35;
            }
          }
          setRelaunchStatus({ ...request, stage: 'blast' });
          if (isPowerUp) playSound('/sounds/space-laser-sound.mp3');
          playSound(
            isPowerUp
              ? '/sounds/large-underwater-explosion-sound.mp3'
              : '/sounds/low-frequency-dice-impact.mp3'
          );
        } else if (progress === 1) {
          if (isPowerUp) {
            const rebuilt = boardTiles[request.index];
            if (rebuilt) {
              rebuilt.square = upgradePowerUpTile(
                current.doorBoard[request.index]
              );
              rebuilt.tile.scale.set(1, 1, 1);
              rebuilt.tile.position.y = -1.31;
              rebuilt.label.visible = true;
              rebuilt.label.scale.set(1, 1, 1);
              rebuilt.material.color.set(0x493117);
              rebuilt.material.emissive.set(0xb98018);
              rebuilt.material.emissiveIntensity = 0.75;
              const texture = makeBoardTexture(
                rebuilt.square,
                false,
                boardTextureOptions
              );
              if (rebuilt.texture) disposeCanvasTexture(rebuilt.texture);
              rebuilt.texture = texture;
              rebuilt.label.material.map = texture;
              rebuilt.label.material.needsUpdate = true;
            }
          }
          const destination = getRelaunchLanding(
            current.doorBoard,
            request.index
          );
          const targetX = ((destination % BOARD_CELLS) - 2) * BOARD_SPACING;
          const targetZ =
            (Math.floor(destination / BOARD_CELLS) - 2) * BOARD_SPACING;
          const flightTime = reduceMotion ? 0.55 : 1.1;
          physics.velocity.set(
            (targetX - root.position.x) / flightTime,
            reduceMotion ? 3.5 : 6.5 + Math.random() * 2,
            (targetZ - root.position.z) / flightTime
          );
          physics.angular.set(
            (Math.random() - 0.5) * 12,
            (Math.random() - 0.5) * 12,
            (Math.random() - 0.5) * 12
          );
          physics.active = true;
          numberLabels.forEach(({ glow }) => {
            glow.visible = false;
          });
          physics.numbersFrozen = true;
          physics.facesRevealed = true;
          physics.onGround = false;
          physics.enteredBoard = true;
          physics.restingTime = 0;
          physics.rollElapsed = 0;
          physics.groundElapsed = 0;
          physics.rollDeadline = reduceMotion ? 1.5 : MAX_ROLL_DURATION;
          physics.impactPlayed = false;
          physics.relaunchStage = 'flight';
          root.scale.setScalar(1);
          startConditionThrow(physics, current.boardCondition);
          setRelaunchStatus({ ...request, stage: 'flight' });
          setBumpStatus((status) => ({
            ...status,
            active: current.allowBumps && status.remaining > 0,
          }));
          playSound('/sounds/fast-swoosh.mp3');
        }
      } else if (current.phase === 'setup') {
        if (current.slingshotPreview) {
          const preview = current.slingshotPreview;
          path.visible = preview.active;
          reticle.visible = preview.active;
          reticleHalo.visible = preview.active;
          const stretch = preview.active ? preview.power : 0;
          root.position.copy(
            getSlingshotOrigin({ ...preview, power: stretch })
          );
          root.rotation.set(
            0.08,
            -preview.angle,
            -Math.sin(preview.angle) * stretch * 0.16
          );
          updateTethers(root.position);
          tetherMaterial.color.set(preview.active ? 0xffe591 : 0x8ff9ff);
          slingCoreMaterial.color.set(preview.active ? 0xffd27a : 0x7df8ff);
          slingAccentMaterial.color.set(preview.active ? 0xffad55 : 0xbca0ff);
          const signature = `${preview.power.toFixed(
            3
          )}:${preview.angle.toFixed(3)}`;
          if (signature !== previewSignature) {
            previewSignature = signature;
            const prediction = getSlingshotPreview(
              preview,
              current.boardCondition
            );
            prediction.points.forEach((point, index) => {
              pathPositions[index * 3] = point.x;
              pathPositions[index * 3 + 1] = point.y;
              pathPositions[index * 3 + 2] = point.z;
              const previous = prediction.points[index - 1];
              pathDistances[index] =
                index === 0
                  ? 0
                  : pathDistances[index - 1] +
                    Math.hypot(
                      point.x - previous.x,
                      point.y - previous.y,
                      point.z - previous.z
                    );
            });
            pathGeometry.attributes.position.needsUpdate = true;
            pathGeometry.attributes.lineDistance.needsUpdate = true;
            reticle.position.set(
              prediction.target.x,
              BOARD_SURFACE_Y + 0.15,
              prediction.target.z
            );
            reticleHalo.position.copy(reticle.position);
          }
          reticleHalo.scale.setScalar(
            reduceMotion ? 1 : 1 + Math.sin(clock.elapsedTime * 3) * 0.1
          );
        } else {
          root.position.y =
            FLOOR_Y + 0.3 + Math.sin(clock.elapsedTime * 1.5) * 0.06;
          root.rotation.y += delta * (0.2 + chargeFactor * 1.6);
          root.rotation.x = Math.sin(clock.elapsedTime * 0.75) * 0.1;
          root.rotation.z = Math.cos(clock.elapsedTime * 0.6) * 0.06;
        }
      } else if (current.phase === 'rolling' && physics.active) {
        physics.rollElapsed += rollDelta;
        let remaining = rollDelta;
        while (remaining > 0) {
          const step = Math.min(PHYSICS_STEP, remaining);
          applyBoardConditionStep(physics, root.position, step);
          physics.onGround = false;
          physics.velocity.y -=
            getConditionGravity(physics.boardCondition) * step;
          root.position.addScaledVector(physics.velocity, step);
          const angularSpeed = physics.angular.length();
          // Relaunches use the same spin and contact physics as the first throw.
          if (angularSpeed > 0.0001) {
            angularStepAxis
              .copy(physics.angular)
              .multiplyScalar(1 / angularSpeed);
            angularStepQuaternion.setFromAxisAngle(
              angularStepAxis,
              angularSpeed * step
            );
            root.quaternion.premultiply(angularStepQuaternion).normalize();
          }
          physics.angular.multiplyScalar(Math.exp(-AIR_SPIN_DAMPING * step));

          const floorContactY = BOARD_SURFACE_Y + getVerticalSupport();
          if (root.position.y < floorContactY) {
            root.position.y = floorContactY;
            physics.numbersFrozen = true;
            physics.onGround = true;
            if (current.concealFacesUntilSettled && !physics.facesRevealed) {
              numberLabels.forEach((_, index) => {
                setFaceValue(
                  index,
                  formatDieValue(
                    current.coreBoss?.damageFaces?.[index] ??
                      rollCoreDamage(CORE_ATTACKS[0].faces)
                  )
                );
              });
              physics.facesRevealed = true;
            }
            if (
              getDie(current.dieType).id === 'chaos' &&
              !physics.facesRevealed
            ) {
              physics.facesRevealed = true;
              numberLabels.forEach((_, index) =>
                presentFaceValue(index, faceValues[index])
              );
            }
            resolveGroundContact(step);
          }

          const edgeLimit = BOARD_EDGE - cubeSize / 2;
          // The Core approaches from the far side; let its projectile enter
          // before those walls contain it, just as the player's near-side throw.
          const coreInbound =
            current.concealFacesUntilSettled &&
            current.coreBoss &&
            !physics.enteredBoard &&
            root.position.z < -edgeLimit;
          if (!coreInbound)
            applySlingshotBounds(physics, root.position, edgeLimit);
          const portalTransit =
            current.portalPair?.armed &&
            !current.concealFacesUntilSettled &&
            applyPortalTransit(
              physics,
              root.position,
              current.portalPair,
              BOARD_SURFACE_Y
            );
          if (portalTransit) {
            physics.portalSnapshot = { ...current.portalPair, used: false };
            physics.portalFlash = 0.65;
            portals.transit(portalTransit.fromIndex, portalTransit.toIndex);
            current.onPortalTransit?.();
            playSound('/sounds/futuristic-zoom.mp3', { preventOverlap: true });
          }
          remaining -= step;
        }

        if (
          physics.onGround &&
          Math.abs(physics.velocity.y) < 0.22 &&
          Math.hypot(physics.velocity.x, physics.velocity.z) < 0.45 &&
          physics.angular.length() < 0.85
        ) {
          physics.restingTime += rollDelta;
        } else {
          physics.restingTime = 0;
        }

        if (
          physics.restingTime > 0.1 &&
          !physics.grounding &&
          !physics.grounded
        ) {
          beginGrounding();
        } else if (
          physics.rollElapsed >= physics.rollDeadline &&
          !physics.grounding &&
          !physics.grounded
        ) {
          // Keep long, high-charge rolls dramatic while settling where the die
          // actually came to rest instead of snapping it to a grid cell.
          const edgeLimit = BOARD_EDGE - cubeSize / 2;
          root.position.x = THREE.MathUtils.clamp(
            root.position.x,
            -edgeLimit,
            edgeLimit
          );
          root.position.z = THREE.MathUtils.clamp(
            root.position.z,
            -edgeLimit,
            edgeLimit
          );
          root.position.y = BOARD_SURFACE_Y + getVerticalSupport();
          physics.velocity.set(0, 0, 0);
          physics.angular.multiplyScalar(0.12);
          beginGrounding();
        }
        if (
          clock.elapsedTime >= nextNumberSwap &&
          !physics.resting &&
          !physics.numbersFrozen &&
          current.concealFacesUntilSettled
        ) {
          nextNumberSwap = clock.elapsedTime + 0.075;
          numberLabels.forEach((_, index) => {
            setFaceValue(
              index,
              current.concealFacesUntilSettled
                ? '?'
                : formatDieValue(Math.floor(Math.random() * 100) + 1)
            );
          });
        }
      } else if (current.phase === 'rolling' && physics.grounding) {
        physics.groundElapsed += rollDelta;
        const progress = Math.min(1, physics.groundElapsed / 0.26);
        const eased = progress * progress * (3 - 2 * progress);
        root.quaternion
          .copy(groundStartQuaternion)
          .slerp(groundTargetQuaternion, eased);
        // Recalculate support after rotating. Using the previous orientation
        // here lets a corner dip below the board during the final settle.
        root.position.y = BOARD_SURFACE_Y + getVerticalSupport();
        if (progress >= 1) {
          physics.grounding = false;
          physics.grounded = true;
          setBumpStatus((status) => ({ ...status, active: false }));
          if (current.rollReady) finishPhysics();
        }
      } else if (current.phase === 'rolling' && physics.grounded) {
        if (current.rollReady) finishPhysics();
      }

      environment.update(current.boardCondition, {
        time: clock.elapsedTime,
        moving:
          current.phase === 'setup' ||
          (current.phase === 'rolling' &&
            !physics.completed &&
            !physics.grounding &&
            !physics.grounded),
        reduceMotion,
      });

      if (physics.resting) {
        physics.flashTime += delta;
        const flash = Math.max(0, 1 - physics.flashTime * 2.2);
        physics.boardPowerTime = Math.max(0, physics.boardPowerTime - delta);
        physics.boardShineTime = Math.max(0, physics.boardShineTime - delta);
        physics.relicPowerTime = Math.max(0, physics.relicPowerTime - delta);
        physics.relicShineTime = Math.max(0, physics.relicShineTime - delta);
        const powerProgress = 1 - physics.boardPowerTime / 0.72;
        const boardPulse =
          physics.boardPowerTime > 0
            ? Math.sin(powerProgress * Math.PI) * 0.22
            : 0;
        const shine =
          physics.boardShineTime > 0 ? physics.boardShineTime / 0.7 : 0;
        const relicPower =
          physics.relicPowerTime > 0 ? physics.relicPowerTime / 1.05 : 0;
        const relicShine =
          physics.relicShineTime > 0 ? physics.relicShineTime / 0.7 : 0;
        root.scale.setScalar(
          1 +
            Math.sin(physics.flashTime * 28) * 0.08 * flash +
            boardPulse +
            shine * 0.08 +
            Math.sin((1 - relicPower) * Math.PI * 4) * 0.1 * relicPower +
            relicShine * 0.12
        );
        cubeMaterial.emissiveIntensity =
          0.7 +
          boardPulse * 8 +
          shine * 7 +
          flash * 0.5 +
          relicPower * 9 +
          relicShine * 13;
        numberLabels.forEach(({ label, glow }, index) => {
          const isTopFace =
            index === physics.topFaceIndex && !physics.guardianFacesReset;
          label.material.opacity = 1;
          glow.visible = isTopFace;
          glow.material.opacity = isTopFace
            ? Math.min(
                1,
                0.65 + flash * 0.7 + shine * 0.5 + relicPower * 0.8 + relicShine
              )
            : 0;
        });
      }

      physics.portalFlash = Math.max(0, physics.portalFlash - delta);
      const chainView =
        current.phase === 'revealed' &&
        !current.relaunch &&
        current.chainReactionTiles?.length > 0;
      chainPreview.update(
        current.phase === 'setup' &&
          current.chainReactionReady &&
          current.slingshotPreview?.active
          ? getChainReactionIndices(
              getSlingshotPreviewTile(
                current.slingshotPreview,
                current.boardCondition
              )
            )
          : chainView
          ? current.chainReactionTiles
          : [],
        clock.elapsedTime,
        reduceMotion
      );
      bountyMarker.update(
        current.bountyTagIndex,
        clock.elapsedTime,
        reduceMotion
      );
      portals.update(
        physics.portalFlash > 0 ? physics.portalSnapshot : current.portalPair,
        physics.portalFlash / 0.65,
        clock.elapsedTime,
        reduceMotion
      );
      physics.bumpTime = Math.max(0, physics.bumpTime - delta);
      const bumpProgress = 1 - physics.bumpTime / BOARD_BUMP_ANIMATION_TIME;
      const bumpPulse =
        physics.bumpTime > 0 ? Math.sin(bumpProgress * Math.PI) : 0;
      // Bumps move the die physically; keep the deck itself stable.
      boardRoot.position.y = 0;
      boardRoot.rotation.set(0, 0, 0);

      const settled = physics.completed;
      treasureTileEffects.update(
        current.treasureChargeIndex,
        clock.elapsedTime,
        reduceMotion,
        boardTiles
      );
      healingTileEffects.update(
        getHealingTileIndex(
          current,
          physics.completed ? physicalLandingRef.current : null
        ),
        clock.elapsedTime,
        reduceMotion,
        boardTiles,
        camera
      );
      const combat = current.encounter?.combat || current.coreBoss?.combat;
      const combatElapsed = combat?.startTime
        ? Math.max(0, Date.now() - combat.startTime)
        : 0;
      if (combat && combatKey !== combat.combatId) {
        combatKey = combat.combatId;
        physics.guardianFacesReset = false;
        // Preserve the last calculation frame before the normal camera moves.
        guardianCamera.begin(root, camera, cameraLookPosition);
      }
      const powerCharging = ['charging', 'blast'].includes(
        physics.relaunchStage
      );
      if (chainView) {
        // Include the entire activated tiles, routed Relay targets, and the
        // settled die/shield rather than framing just the die's center.
        chainBounds.makeEmpty();
        current.chainReactionTiles.forEach((index) => {
          if (!boardTiles[index]) return;
          const x = ((index % BOARD_CELLS) - 2) * BOARD_SPACING;
          const z = (Math.floor(index / BOARD_CELLS) - 2) * BOARD_SPACING;
          chainBounds.expandByPoint(
            chainPoint.set(
              x - CHAIN_TILE_RADIUS,
              BOARD_SURFACE_Y - 0.2,
              z - CHAIN_TILE_RADIUS
            )
          );
          chainBounds.expandByPoint(
            chainPoint.set(
              x + CHAIN_TILE_RADIUS,
              BOARD_SURFACE_Y + 0.3,
              z + CHAIN_TILE_RADIUS
            )
          );
        });
        const dieRadius = activeShieldAmount > 0 ? 1.6 : 0.9;
        chainBounds.expandByPoint(
          chainPoint.copy(root.position).addScalar(-dieRadius)
        );
        chainBounds.expandByPoint(
          chainPoint.copy(root.position).addScalar(dieRadius)
        );
        chainBounds.getCenter(chainCenter);
      }
      if (current.phase === 'setup') {
        const diePulse = reduceMotion
          ? 0
          : (Math.sin(clock.elapsedTime * 3.2) + 1) * 0.12;
        cubeMaterial.emissiveIntensity = 1.1 + diePulse + chargeFactor * 1.2;
        dieGlow.intensity =
          2.6 + diePulse * 5 + (aiming ? 0 : chargeFactor * 4);
      } else if (!physics.resting) {
        cubeMaterial.emissiveIntensity = 0.78 + chargeFactor * 0.6;
        dieGlow.intensity = 1.25 + chargeFactor * 1.2;
      } else {
        dieGlow.intensity = 1.45;
      }
      const mobileRoll =
        mobileViewport?.matches &&
        boardTiles.length > 0 &&
        current.phase === 'rolling' &&
        !settled;
      // Keep the die and its neighboring tiles readable as the throw slows.
      // Bumps extend the throw without restarting the camera's progress.
      const mobileZoomProgress =
        mobileRoll && !reduceMotion
          ? THREE.MathUtils.smoothstep(
              physics.rollElapsed,
              0.15,
              MOBILE_ROLL_ZOOM_DURATION
            )
          : 0;
      const followProgress = current.coreBoss ? 1 : mobileZoomProgress;
      const boardFollowView =
        boardTiles.length > 0 &&
        !aiming &&
        !chainView &&
        !combat &&
        !counterView &&
        (current.phase === 'rolling' ||
          (current.phase === 'revealed' && current.focusSettledDie));
      sling.visible = aiming;
      if (!counterView && !boardFollowView) {
        camera.position.lerp(
          new THREE.Vector3(
            chainView ? chainCenter.x : aiming ? 0 : root.position.x,
            aiming
              ? setupCameraPosition.y
              : chainView
              ? SETUP_CAMERA_HEIGHT
              : BOARD_CAMERA_HEIGHT + root.position.y,
            aiming
              ? setupCameraPosition.z
              : chainView
              ? BOARD_CAMERA_DISTANCE + chainCenter.z
              : BOARD_CAMERA_DISTANCE +
                root.position.z *
                  (settled || powerCharging
                    ? 1
                    : THREE.MathUtils.lerp(0.12, 1, followProgress))
          ),
          chainView && reduceMotion ? 1 : Math.min(1, delta * 5)
        );
        cameraLookTarget.set(
          chainView ? chainCenter.x : aiming ? 0 : root.position.x,
          chainView
            ? BOARD_SURFACE_Y + 0.35
            : current.phase === 'revealed' && current.focusSettledDie
            ? root.position.y
            : aiming
            ? setupLookTarget.y
            : root.position.y - 0.4,
          aiming
            ? setupLookTarget.z
            : chainView
            ? chainCenter.z
            : root.position.z *
              (settled || powerCharging
                ? 1
                : THREE.MathUtils.lerp(0.04, 1, followProgress))
        );
        if (mobileRoll && !reduceMotion) {
          cameraLookPosition.lerp(cameraLookTarget, Math.min(1, delta * 5));
        } else {
          cameraLookPosition.copy(cameraLookTarget);
        }
        camera.lookAt(cameraLookPosition);
        const previousZoom = camera.zoom;
        if (powerCharging) {
          physics.targetCameraZoom = 2.1;
        } else if (aiming) {
          physics.targetCameraZoom = getSetupCameraZoom(current.boardCondition);
        } else if (
          current.phase === 'rolling' &&
          (current.launch || mobileRoll || current.coreBoss) &&
          !physics.completed
        ) {
          const boardZoom =
            Math.min(1, Math.max(0.42, camera.aspect * 0.92)) *
            (window.innerWidth > 600 ? 1.12 : 1);
          const dieZoom = THREE.MathUtils.clamp(camera.aspect * 1.5, 1.35, 1.8);
          physics.targetCameraZoom = THREE.MathUtils.lerp(
            boardZoom,
            dieZoom,
            mobileZoomProgress
          );
        }
        if (chainView) {
          camera.updateMatrixWorld(true);
          const halfFov = Math.tan(THREE.MathUtils.degToRad(camera.fov / 2));
          let fitZoom = 6.2;
          const frameBox = (x, y, z, halfX, halfY, halfZ) => {
            [-1, 1].forEach((dx) => {
              [-1, 1].forEach((dy) => {
                [-1, 1].forEach((dz) => {
                  chainPoint
                    .set(x + dx * halfX, y + dy * halfY, z + dz * halfZ)
                    .applyMatrix4(camera.matrixWorldInverse);
                  const halfHeight = Math.max(0.01, -chainPoint.z) * halfFov;
                  fitZoom = Math.min(
                    fitZoom,
                    (CHAIN_VIEW_FILL * halfHeight * camera.aspect) /
                      Math.max(0.01, Math.abs(chainPoint.x)),
                    (CHAIN_VIEW_FILL * halfHeight) /
                      Math.max(0.01, Math.abs(chainPoint.y))
                  );
                });
              });
            });
          };
          // Fit the actual cross/Relay tiles, excluding the empty corners of
          // their bounding rectangle, and use more of the cleared viewport.
          current.chainReactionTiles.forEach((index) => {
            if (!boardTiles[index]) return;
            frameBox(
              ((index % BOARD_CELLS) - 2) * BOARD_SPACING,
              BOARD_SURFACE_Y + 0.05,
              (Math.floor(index / BOARD_CELLS) - 2) * BOARD_SPACING,
              CHAIN_TILE_RADIUS,
              0.25,
              CHAIN_TILE_RADIUS
            );
          });
          const dieRadius = activeShieldAmount > 0 ? 1.6 : 0.9;
          frameBox(
            root.position.x,
            root.position.y,
            root.position.z,
            dieRadius,
            dieRadius,
            dieRadius
          );
          physics.targetCameraZoom = fitZoom;
          // Pull out before the first tile pulse; subsequent panning can ease
          // without cropping any tile, even on a narrow portrait viewport.
          camera.zoom = Math.min(camera.zoom, fitZoom);
        } else if (current.phase === 'revealed' && current.focusSettledDie) {
          const height = Math.max(1, mount.clientHeight);
          const width = Math.max(1, mount.clientWidth);
          physics.targetCameraZoom = THREE.MathUtils.clamp(
            Math.min(1850 / height, (7 * width) / height),
            1.85,
            6.2
          );
          if (activeShieldAmount > 0) {
            // Frame the crest and its orbiting sparks, including short screens.
            const shieldDistance = Math.max(
              1,
              camera.position.distanceTo(root.position) - 0.92
            );
            const shieldZoom =
              (Math.tan(THREE.MathUtils.degToRad(camera.fov / 2)) *
                shieldDistance *
                Math.min(1, camera.aspect)) /
              1.45;
            physics.targetCameraZoom = Math.min(
              physics.targetCameraZoom,
              shieldZoom
            );
          }
        }
        if (Number.isInteger(current.treasureChargeIndex)) {
          const treasureZoom = Math.max(
            1.1,
            Math.min(2.2, camera.aspect * 1.8)
          );
          physics.targetCameraZoom = Math.min(
            physics.targetCameraZoom,
            treasureZoom
          );
          camera.zoom = Math.min(camera.zoom, treasureZoom);
        }
        if (healingTileEffects.active) {
          const healingZoom = Math.max(1.1, Math.min(2.4, camera.aspect * 1.8));
          physics.targetCameraZoom = Math.min(
            physics.targetCameraZoom,
            healingZoom
          );
          camera.zoom = Math.min(camera.zoom, healingZoom);
        }
        camera.zoom +=
          (physics.targetCameraZoom - camera.zoom) * Math.min(1, delta * 3.5);
        if (Math.abs(previousZoom - camera.zoom) > 0.001)
          camera.updateProjectionMatrix();
      }

      if (arriving) {
        if (current.phase !== 'setup' || !current.arrival) {
          arriving = false;
          current.onArrivalComplete?.();
        } else {
          const progress =
            arrivalStartedAt === null
              ? 0
              : Math.min(
                  1,
                  (performance.now() - arrivalStartedAt) /
                    (arrivalSeconds * 1000)
                );
          const eased = progress * progress * (3 - 2 * progress);
          const width = Math.max(1, mount.clientWidth);
          const height = Math.max(1, mount.clientHeight);
          const fullZoom = getSetupCameraZoom(current.boardCondition);
          if (arrivalHandoff) {
            arrivalCameraStart.copy(arrivalHandoff.position);
          } else {
            arrivalCameraStart.copy(root.position).add(arrivalCameraOffset);
          }
          camera.position.lerpVectors(
            arrivalCameraStart,
            arrivalCameraEnd,
            eased
          );
          cameraLookPosition.lerpVectors(
            arrivalHandoff?.look || root.position,
            arrivalLookEnd,
            eased
          );
          camera.zoom = THREE.MathUtils.lerp(
            arrivalHandoff?.zoom || 1,
            fullZoom,
            eased
          );
          const bounds = mount.getBoundingClientRect();
          // Crop the previous full-screen shot into the aiming viewport before
          // easing its projection out. Changing canvases must not snap the die.
          camera.setViewOffset(
            THREE.MathUtils.lerp(arrivalHandoff?.width || width, width, eased),
            THREE.MathUtils.lerp(
              arrivalHandoff?.height || height,
              height,
              eased
            ),
            (bounds.left -
              (arrivalHandoff?.left ?? bounds.left) +
              (arrivalHandoff?.offsetX || 0)) *
              (1 - eased),
            THREE.MathUtils.lerp(
              bounds.top -
                (arrivalHandoff?.top ?? bounds.top) +
                (arrivalHandoff?.offsetY || 0),
              setupFrameOffsetY,
              eased
            ),
            width,
            height
          );
          camera.lookAt(cameraLookPosition);
          camera.updateProjectionMatrix();
          if (progress === 1) {
            arriving = false;
            current.onArrivalComplete?.();
          }
        }
      }

      // Local die tracking owns its projection during throws and calculations.
      if (
        boardTiles.length &&
        !current.coreBoss &&
        !boardFollowView &&
        current.phase !== 'setup' &&
        !arriving &&
        !combat
      ) {
        const radius = activeShieldAmount > 0 ? 1.65 : 0.95;
        movingDiePoints.forEach((point, index) =>
          point.set(
            root.position.x + (index & 1 ? radius : -radius),
            root.position.y + (index & 2 ? radius : -radius),
            root.position.z + (index & 4 ? radius : -radius)
          )
        );
        physics.targetCameraZoom = fitBoardCamera(
          camera,
          allFramePoints,
          Math.max(1, mount.clientWidth),
          Math.max(1, mount.clientHeight),
          {
            top: Math.min(
              current.coreBoss?.hideReadout
                ? 8
                : current.coreBoss
                ? Math.max(
                    85,
                    (boardReadoutsRef.current?.offsetHeight || 0) + 24
                  )
                : 60,
              mount.clientHeight * 0.25
            ),
            bottom: Math.min(
              current.phase === 'revealed' ? 115 : 75,
              mount.clientHeight * 0.18
            ),
            side: Math.min(16, mount.clientWidth * 0.025),
          }
        );
      }

      if (boardFollowView) {
        boardDieCamera.update({
          camera,
          look: cameraLookPosition,
          position: root.position,
          velocity: physics.velocity,
          delta,
          settled:
            settled ||
            physics.grounding ||
            physics.grounded ||
            powerCharging ||
            current.phase === 'revealed',
          reduceMotion,
        });
        physics.targetCameraZoom = camera.zoom;
      }

      if (counterView) {
        if (counterCameraStage !== current.coreBoss.stage) {
          counterCameraStage = current.coreBoss.stage;
          counterCameraStageStartedAt = clock.elapsedTime;
        }
        // Look past the die toward the Core from its right shoulder. Frame
        // the entire attacker and defender so the attack's origin stays clear.
        counterCameraPosition
          .set(0.9, 1.9, 5.8)
          .multiplyScalar(Math.max(1, 0.55 / camera.aspect))
          .add(defender.position);
        counterCameraLook.lerpVectors(
          defender.position,
          CORE_BOARD_POSITION,
          0.35
        );
        counterDiePoints.forEach((point, index) =>
          point.set(
            defender.position.x + (index & 1 ? 1.65 : -1.65),
            defender.position.y + (index & 2 ? 1.65 : -1.65),
            defender.position.z + (index & 4 ? 1.65 : -1.65)
          )
        );
        // Later heart forms extend their cables beyond the shell's bounds.
        const heartRadius =
          current.coreBoss.phase === 'meltdown'
            ? 10.4
            : current.coreBoss.phase === 'evolution'
            ? 7
            : 0;
        counterHeartPoints.forEach((point, index) => {
          const angle = (index * Math.PI * 2) / counterHeartPoints.length;
          point.set(
            CORE_BOARD_POSITION.x + Math.cos(angle) * heartRadius,
            CORE_BOARD_POSITION.y + Math.sin(angle) * heartRadius * 0.88,
            CORE_BOARD_POSITION.z + 2
          );
        });
        counterFramingCamera.copy(camera);
        counterFramingCamera.position.copy(counterCameraPosition);
        counterFramingCamera.lookAt(counterCameraLook);
        const width = Math.max(1, mount.clientWidth);
        const height = Math.max(1, mount.clientHeight);
        const counterZoom = fitBoardCamera(
          counterFramingCamera,
          counterFramePoints,
          width,
          height,
          {
            top: Math.min(120, height * 0.25),
            bottom: Math.min(210, height * 0.32),
            side: Math.min(20, width * 0.04),
          }
        );
        const returning =
          current.coreBoss.stage === 'bossOutcome'
            ? reduceMotion
              ? 1
              : THREE.MathUtils.smoothstep(
                  clock.elapsedTime - counterCameraStageStartedAt,
                  0.2,
                  getCoreLaserDuration('bossOutcome') / 1000 - 0.15
                )
            : 0;
        counterCameraPosition.lerp(arrivalCameraEnd, returning);
        counterCameraLook.lerp(arrivalLookEnd, returning);
        // The return already has a time-based ease; a second lerp would lag
        // behind its final ready frame when the cinematic unmounts.
        const ease = reduceMotion || returning > 0 ? 1 : Math.min(1, delta * 6);
        camera.position.lerp(counterCameraPosition, ease);
        cameraLookPosition.lerp(counterCameraLook, ease);
        camera.lookAt(cameraLookPosition);
        const previousOffsetX = camera.view?.enabled ? camera.view.offsetX : 0;
        const previousOffsetY = camera.view?.enabled ? camera.view.offsetY : 0;
        camera.zoom = THREE.MathUtils.lerp(
          camera.zoom,
          THREE.MathUtils.lerp(
            counterZoom,
            getSetupCameraZoom(current.boardCondition),
            returning
          ),
          ease
        );
        camera.setViewOffset(
          width,
          height,
          THREE.MathUtils.lerp(
            previousOffsetX,
            counterFramingCamera.view.offsetX * (1 - returning),
            ease
          ),
          THREE.MathUtils.lerp(
            previousOffsetY,
            THREE.MathUtils.lerp(
              counterFramingCamera.view.offsetY,
              setupFrameOffsetY,
              returning
            ),
            ease
          ),
          width,
          height
        );
        camera.updateProjectionMatrix();
        if (current.coreBoss.cameraHandoffRef) {
          const bounds = mount.getBoundingClientRect();
          current.coreBoss.cameraHandoffRef.current = {
            position: camera.position.clone(),
            look: cameraLookPosition.clone(),
            zoom: camera.zoom,
            width: mount.clientWidth,
            height: mount.clientHeight,
            left: bounds.left,
            top: bounds.top,
            offsetX: camera.view?.enabled ? camera.view.offsetX : 0,
            offsetY: camera.view?.enabled ? camera.view.offsetY : 0,
          };
        }
      }

      // Pull feedback belongs on the die. Relighting the entire deck on every
      // pointer move makes power adjustments look like board/background flashes.
      const environmentCharge = aiming ? 0 : chargeFactor;
      cyanLight.intensity = current.critical
        ? 7 + environmentCharge * 6
        : 3.5 + environmentCharge * 3;
      cyanLight.intensity += bumpPulse * 3;
      violetLight.intensity = 2.5 + environmentCharge * 3.5;
      criticalLight.intensity = current.critical
        ? 4 + Math.sin(clock.elapsedTime * 12) * 2
        : 0;
      const setupDieControl = propsRef.current.setupDieControlRef?.current;
      if (setupDieControl && current.phase === 'setup' && !arriving) {
        camera.updateMatrixWorld();
        scene.updateMatrixWorld();
        projectedDie.copy(root.position).project(camera);
        if (
          projectedDie.z >= -1 &&
          projectedDie.z <= 1 &&
          mount.clientWidth > 0 &&
          mount.clientHeight > 0
        ) {
          const mountBounds = mount.getBoundingClientRect();
          const boardBounds =
            setupDieControl.parentElement?.getBoundingClientRect();
          setupDieControl.style.left = `${
            (boardBounds ? mountBounds.left - boardBounds.left : 0) +
            (projectedDie.x * 0.5 + 0.5) * mount.clientWidth
          }px`;
          setupDieControl.style.top = `${
            (boardBounds ? mountBounds.top - boardBounds.top : 0) +
            (-projectedDie.y * 0.5 + 0.5) * mount.clientHeight
          }px`;
          setupDieControl.hidden = false;
        } else {
          setupDieControl.hidden = true;
        }
      } else if (setupDieControl) {
        setupDieControl.hidden = true;
      }
      const setupTileControls = current.setupTileControlRef?.current;
      if (setupTileControls && current.phase === 'setup' && !arriving) {
        camera.updateMatrixWorld();
        scene.updateMatrixWorld();
        setupTileControls.updateProjection(
          boardTiles,
          camera,
          renderer.domElement
        );
      }
      if (physics.relaunchStage) {
        cubeMaterial.emissive.set(
          physics.relaunchRequest?.type === 'gambit' ? 0xff8b73 : 0xffb72e
        );
        cubeMaterial.emissiveIntensity = powerCharging ? 1.8 : 1.1;
        dieGlow.color.set(
          physics.relaunchRequest?.type === 'gambit' ? 0xffbc76 : 0xffdf67
        );
        dieGlow.intensity = powerCharging ? 6 : 3;
      } else {
        const coreSkin = getCoreDieAppearance(current.corePhase);
        cubeMaterial.emissive.set(
          current.concealFacesUntilSettled
            ? coreSkin.emissive
            : getDie(current.dieType).emissive
        );
        dieGlow.color.set(
          current.concealFacesUntilSettled
            ? coreSkin.light
            : getDie(current.dieType).color
        );
      }
      coreDieEffects?.update({
        phase: current.corePhase,
        elapsed: clock.elapsedTime,
        camera,
        moving: current.phase === 'rolling' && !physics.completed,
        reduceMotion,
        visible: current.concealFacesUntilSettled && root.visible,
      });
      styleAura.update({
        position: root.position,
        style: current.attackStyle,
        elapsed: clock.elapsedTime,
        delta,
        reduceMotion,
        moving: current.phase === 'rolling',
        visible: root.visible && !current.concealFacesUntilSettled,
      });
      // Resolved totals also include shields reached through Relay,
      // and Chain Reaction. The amount check avoids replaying a direct landing.
      if (physics.completed && !physics.relaunchStage) {
        activateShield(current.shieldAmount);
      }
      shieldEffects.update({
        diePosition: root.position,
        camera,
        delta,
        reduceMotion,
        visible: root.visible && physics.completed && current.phase !== 'setup',
      });
      powerUpEffects.update({
        stage:
          physics.relaunchRequest?.type === 'powerUp'
            ? physics.relaunchStage
            : null,
        elapsed:
          physics.relaunchStage === 'flight'
            ? POWER_UP_BLAST_SECONDS + physics.rollElapsed
            : physics.relaunchElapsed,
        progress: Math.min(
          1,
          physics.relaunchElapsed /
            (reduceMotion ? 0.25 : POWER_UP_CHARGE_SECONDS)
        ),
        diePosition: root.position,
        tilePosition: physics.relaunchOrigin,
        reduceMotion,
      });
      gambitEffects.update({
        stage:
          physics.relaunchRequest?.type === 'gambit'
            ? physics.relaunchStage
            : null,
        elapsed:
          physics.relaunchStage === 'flight'
            ? physics.rollElapsed
            : physics.relaunchElapsed,
        progress:
          physics.relaunchStage === 'flight'
            ? 1
            : Math.min(
                1,
                physics.relaunchElapsed /
                  (physics.relaunchStage === 'charging'
                    ? reduceMotion
                      ? 0.08
                      : 0.34
                    : reduceMotion
                    ? 0.05
                    : 0.2)
              ),
        diePosition: root.position,
      });
      const hitProgress = combat
        ? THREE.MathUtils.clamp((combatElapsed - combat.hitAt) / 650, 0, 1)
        : 0;
      guardianScene.update({
        enemy: current.encounter?.enemy,
        elapsed: clock.elapsedTime,
        reduceMotion,
        hit: hitProgress,
        death:
          combat && combat.enemyHpAfter === 0
            ? THREE.MathUtils.clamp(
                (combatElapsed - combat.hitAt - 120) /
                  (reduceMotion ? 300 : 1100),
                0,
                1
              )
            : 0,
        retaliation:
          combat?.retaliation && combatElapsed >= combat.counterWindupAt
            ? THREE.MathUtils.clamp(
                (combatElapsed - combat.counterWindupAt) /
                  (combat.counterLaunchAt - combat.counterWindupAt),
                0,
                1
              ) *
              (1 -
                THREE.MathUtils.smoothstep(
                  combatElapsed,
                  combat.counterLaunchAt,
                  combat.counterAt
                ))
            : 0,
      });
      if (combat) {
        guardianCamera.update({
          die: root,
          camera,
          look: cameraLookPosition,
          elapsed: combatElapsed,
          combat,
          reduceMotion,
          charge: chargeFactor,
          overclock: combat.relicsBefore?.includes('overclock') || false,
          zoom: window.innerWidth > 600 ? 1.12 : 1,
          target: current.coreBoss
            ? CORE_BOARD_STRIKE_TARGET
            : GUARDIAN_POSITION,
          followDistance: current.coreBoss ? 8.6 : undefined,
          portraitWidth: current.coreBoss ? 0.95 : undefined,
          frameTarget: current.coreBoss ? CORE_BOARD_POSITION : null,
          targetRadius: current.coreBoss ? CORE_BOARD_FRAME_RADIUS : 0,
          getReadyFrame: () => ({
            position: arrivalCameraEnd,
            look: arrivalLookEnd,
            zoom: getSetupCameraZoom(current.boardCondition),
            offsetY: setupFrameOffsetY,
            width: Math.max(1, mount.clientWidth),
            height: Math.max(1, mount.clientHeight),
          }),
        });
        if (combatElapsed >= combat.returnAt) {
          if (!physics.guardianFacesReset) {
            // The outgoing die carries its calculated damage through impact.
            // Restore the ready faces on the way home without changing the
            // underlying roll, Power Up values, or committed attack damage.
            const defaults =
              current.dieFaceValues ||
              getDie(current.dieType).faces ||
              Array(6).fill('?');
            physics.guardianFacesReset = true;
            physics.valueTween = null;
            numberLabels.forEach(({ label, glow, defaultRotation }, index) => {
              presentFaceValue(index, formatDieValue(defaults[index]));
              label.rotation.copy(defaultRotation);
              glow.rotation.copy(defaultRotation);
              glow.visible = false;
              glow.material.opacity = 0;
            });
          }
          sling.visible = true;
          updateTethers(SLINGSHOT_ORIGIN);
        }
        if (
          combat.returnsToSetup &&
          !reduceMotion &&
          combatElapsed >= combat.returnEndAt &&
          current.encounter?.cameraHandoffRef
        ) {
          const bounds = mount.getBoundingClientRect();
          current.encounter.cameraHandoffRef.current = {
            position: camera.position.clone(),
            look: cameraLookPosition.clone(),
            zoom: camera.zoom,
            width: mount.clientWidth,
            height: mount.clientHeight,
            left: bounds.left,
            top: bounds.top,
          };
        }
      }
      if (current.coreBoss?.combat && combat?.startTime) {
        if (
          combatElapsed >= combat.hitAt &&
          coreContactNotified !== combat.combatId
        ) {
          coreContactNotified = combat.combatId;
          current.coreBoss.onPlayerImpact?.();
        }
        if (
          combatElapsed >= combat.returnEndAt &&
          coreReturnNotified !== combat.combatId
        ) {
          coreReturnNotified = combat.combatId;
          current.coreBoss.onStrikeComplete?.();
        }
      }
      guardianStrike.update({
        combat,
        elapsed: combatElapsed,
        die: root,
        camera,
        reduceMotion,
      });
      trapEffects.update({
        effect: current.trapEffect,
        position: root.position,
        elapsed: clock.elapsedTime,
        visible: root.visible && physics.completed && current.phase !== 'setup',
        reduceMotion,
      });
      guardianCounter.update({
        enemy: current.encounter?.enemy,
        combat,
        elapsed: combatElapsed,
        target: root.position,
        camera,
        reduceMotion,
      });
      if (current.coreBoss?.spell) {
        camera.position.set(0, 14, 18);
        camera.lookAt(0, 0, -4);
        camera.clearViewOffset();
        fitBoardCamera(
          camera,
          boardFramePoints,
          Math.max(1, mount.clientWidth),
          Math.max(1, mount.clientHeight),
          { top: 12, bottom: 12, side: 12 }
        );
      }
      coreBoardScene.update({
        boss: current.coreBoss,
        elapsed: clock.elapsedTime,
        delta,
        camera,
        reduceMotion,
        target: defender?.position,
      });
      if (current.coreBoss?.spell) {
        root.visible = false;
        const spell = current.coreBoss.spell;
        const progress = getCoreSpellProgress(spell);
        spell.changes.forEach(({ index, after }) => {
          const tile = boardTiles[index];
          if (
            !tile ||
            tile.square.effect?.type === 'corruption' ||
            (!reduceMotion && progress < getCoreSpellRevealAt(spell, index))
          )
            return;
          tile.square = after;
          tile.material.color.set(0x37104f);
          tile.material.emissive.set(0x9a28ce);
          tile.material.emissiveIntensity = 0.9;
          const texture = makeBoardTexture(after, false, boardTextureOptions);
          if (tile.texture) disposeCanvasTexture(tile.texture);
          tile.texture = texture;
          tile.label.material.map = texture;
          tile.label.material.needsUpdate = true;
        });
      }
      defenderAura?.update({
        position: defender.position,
        style: current.coreBoss?.playerDie?.style,
        elapsed: clock.elapsedTime,
        delta,
        reduceMotion,
        visible: defender.visible,
      });
      if (current.encounter || current.coreBoss) {
        camera.updateMatrixWorld();
        combatPoint
          .copy(current.coreBoss ? CORE_BOARD_POSITION : GUARDIAN_POSITION)
          .add(
            new THREE.Vector3(
              0,
              current.coreBoss ? CORE_BOARD_FRAME_RADIUS : 4.6,
              0
            )
          )
          .project(camera);
        mount.style.setProperty(
          '--guardian-x',
          THREE.MathUtils.clamp(combatPoint.x * 0.5 + 0.5, 0.2, 0.8) *
            mount.clientWidth +
            'px'
        );
        mount.style.setProperty(
          '--guardian-y',
          Math.max(
            8,
            (-combatPoint.y * 0.5 + 0.5) * mount.clientHeight -
              (guardianReadoutRef.current?.offsetHeight || 54) +
              16
          ) + 'px'
        );
        combatPoint.copy(
          current.coreBoss ? CORE_BOARD_STRIKE_TARGET : GUARDIAN_POSITION
        );
        combatPoint.y += current.coreBoss ? 0.35 : 1;
        combatPoint.project(camera);
        mount.style.setProperty(
          '--hit-x',
          THREE.MathUtils.clamp(combatPoint.x * 0.5 + 0.5, 0.2, 0.8) *
            mount.clientWidth +
            'px'
        );
        mount.style.setProperty(
          '--hit-y',
          THREE.MathUtils.clamp(-combatPoint.y * 0.5 + 0.5, 0.18, 0.7) *
            mount.clientHeight +
            'px'
        );
        combatPoint.copy(defender?.position || root.position);
        combatPoint.y += 0.4;
        combatPoint.project(camera);
        mount.style.setProperty(
          '--player-hit-x',
          THREE.MathUtils.clamp(combatPoint.x * 0.5 + 0.5, 0.2, 0.8) *
            mount.clientWidth +
            'px'
        );
        mount.style.setProperty(
          '--player-hit-y',
          THREE.MathUtils.clamp(-combatPoint.y * 0.5 + 0.5, 0.18, 0.7) *
            mount.clientHeight +
            'px'
        );
      }
      renderer.render(scene, camera);
      if (combat && !combat.startTime) current.encounter?.onCombatReady?.();
      // Shader compilation on the first frame should not consume the pullback.
      if (arriving && arrivalStartedAt === null)
        arrivalStartedAt = performance.now();
    };
    const onContextLost = (event) => {
      event.preventDefault();
      graphicsLost = true;
      cancelAnimationFrame(frame);
      renderer.domElement.style.visibility = 'hidden';
      renderer.dispose();
      renderer.domElement.width = 1;
      renderer.domElement.height = 1;
      setWebglAvailable(false);
      // Continue the saved combat transaction through the existing fallback;
      // do not remount the battle or ask the player to reveal the round again.
      propsRef.current.encounter?.onCombatReady?.();
      propsRef.current.onArrivalComplete?.();
    };
    renderer.domElement.addEventListener('webglcontextlost', onContextLost);
    animate();

    return () => {
      cancelAnimationFrame(frame);
      renderer.domElement.removeEventListener(
        'webglcontextlost',
        onContextLost
      );
      resizeSceneRef.current = null;
      stopObservingSize();
      window.removeEventListener('keydown', onBumpKeyDown);
      window.removeEventListener('keydown', onAnchorKeyDown);
      renderer.domElement.removeEventListener(
        'pointerdown',
        onBoardPointerDown
      );
      bumpHandlerRef.current = null;
      phaseAnchorHandlerRef.current = null;
      coreDieEffects?.dispose();
      shieldEffects.dispose();
      trapEffects.dispose();
      styleAura.dispose();
      defenderAura?.dispose();
      guardianCounter.dispose();
      guardianStrike.dispose();
      disposeSceneResources(scene);
      if (!graphicsLost) {
        renderer.dispose();
        renderer.forceContextLoss?.();
        renderer.domElement.width = 1;
        renderer.domElement.height = 1;
      }
      if (renderer.domElement.parentNode === mount)
        mount.removeChild(renderer.domElement);
    };
  }, [reportPhysicalLanding]);

  const ghostThrow =
    !allowBumps &&
    concealFacesUntilSettled &&
    (ghostBumpArmed || ghostBumpUsed);
  const bumpLimit = ghostThrow ? GHOST_BUMP_LIMIT : MAX_BOARD_BUMPS;
  const showBumpControls =
    (allowBumps || ghostThrow) &&
    phase === 'rolling' &&
    webglAvailable &&
    doorBoard?.length > 0;
  const bumpsAvailable =
    showBumpControls && bumpStatus.active && bumpStatus.remaining > 0;
  const showAnchorControls =
    showBumpControls && (phaseAnchorArmed || anchorStatus.used);
  const bumpButtonLabel = ghostThrow
    ? `Ghost bump Core die, ${bumpStatus.remaining} ${
        bumpStatus.remaining === 1 ? 'bump' : 'bumps'
      } left`
    : `Bump board, ${bumpStatus.remaining} bumps left`;
  const icyTiles = getIceTileIndices(boardCondition);
  const rebuiltPowerUpMultiplier =
    relaunch?.type === 'powerUp'
      ? getPowerUpMultiplier(
          upgradePowerUpTile(doorBoard?.[relaunch.index]).effect
        )
      : null;
  const displayedShieldAmount = webglAvailable
    ? shieldStatus
    : phase === 'revealed'
    ? shieldAmount
    : 0;
  const shieldNotice = displayedShieldAmount > 0 && (
    <div className="coreboundShieldStatus" role="status" aria-live="polite">
      <BoardShieldIcon />
      <span>
        <strong>
          {concealFacesUntilSettled ? 'CORE SHIELD' : 'SHIELD ACTIVE'}
        </strong>
        <small>Blocks up to {displayedShieldAmount} damage</small>
      </span>
    </div>
  );

  return (
    <div
      ref={mountRef}
      className={`coreboundWebglStage ${
        coreBoss ? 'coreboundWebglHasCore' : ''
      } ${usesMobileRendering() ? 'is-mobile-rendering' : ''} ${
        webglAvailable ? '' : 'coreboundWebglFallback'
      } ${concealFacesUntilSettled ? 'coreboundCoreDieRoll' : ''} ${
        critical ? 'coreboundWebglCritical' : ''
      } ${flipped ? 'coreboundWebglFlipped' : ''} ${
        boardPowering ? 'coreboundBoardPowering' : ''
      } ${boardShine ? 'coreboundBoardShining' : ''} ${
        relicPowering ? 'coreboundRelicPowering' : ''
      } ${relicShine ? 'coreboundRelicShining' : ''} ${
        bumpsAvailable ? 'coreboundBoardBumpAvailable' : ''
      } ${anchorStatus.active ? 'coreboundPhaseAnchored' : ''} ${
        ghostBumpUsed ? 'coreboundGhostBumpSpent' : ''
      }`}
      aria-label="Three-dimensional Corebound dice roll"
      data-board-condition={boardCondition?.type || 'stable'}
      data-treasure-charging={
        Number.isInteger(treasureChargeIndex) || undefined
      }
      data-die={concealFacesUntilSettled ? 'core' : getDie(dieType).id}
      data-attack-style={attackStyle || undefined}
      data-die-faces={dieFaceValues?.join(',')}
      data-core-die-phase={
        concealFacesUntilSettled
          ? getCoreDieAppearance(corePhase).phase
          : undefined
      }
    >
      <FracturedCoreBackdrop
        sector={coreBoss ? 10 : sector}
        charge={phase === 'setup' && !slingshotPreview ? charge : 0}
      />
      <div className="coreboundBoardReadouts" ref={boardReadoutsRef}>
        <CanvasHealthBars
          boss={coreBoss?.hideReadout ? null : coreBoss}
          bossReadoutRef={coreBoss ? guardianReadoutRef : undefined}
          reduceMotion={reduceMotion}
        />
        {encounter && (
          <div
            className={`coreboundGuardianReadout ${
              encounter.combat?.enemyHpAfter === 0 &&
              encounter.combat.stage !== 'launch'
                ? 'is-defeated'
                : ''
            }`}
            ref={guardianReadoutRef}
          >
            <SectorEnemy
              {...encounter}
              board={false}
              showArt={!webglAvailable}
              reduceMotion={reduceMotion}
            />
          </div>
        )}
        {encounter?.combat?.retaliation &&
          ['windup', 'incoming'].includes(encounter.combat.stage) && (
            <div
              className="coreboundGuardianCounterCue"
              style={{ '--enemy-color': encounter.enemy.color }}
              role="status"
            >
              <small>
                {encounter.combat.stage === 'windup'
                  ? `${encounter.enemy.name.toUpperCase()} IS CHARGING`
                  : 'INCOMING ATTACK'}
              </small>
              <strong>
                {
                  getGuardianCounterAttack(
                    encounter.enemy,
                    encounter.combat.attacks
                  ).name
                }
              </strong>
              <span>
                {encounter.combat.stage === 'windup'
                  ? encounter.enemy.shape === 'warden'
                    ? 'A fracture opens. Rift blades are forming…'
                    : getGuardianCounterAttack(
                        encounter.enemy,
                        encounter.combat.attacks
                      ).telegraph
                  : 'Brace for impact'}
              </span>
            </div>
          )}
        {boardStatus}
        {healingCue && (
          <div
            key={healingCue.index}
            className="coreboundHealingTileCue"
            role="status"
            aria-live="polite"
          >
            <span aria-hidden="true">+</span>
            <div>
              <small>RECOVERY TILE</small>
              <strong>+{healingCue.amount} HP</strong>
            </div>
          </div>
        )}
        {Number.isInteger(treasureChargeIndex) && (
          <div className="coreboundTreasureTileCue" role="status">
            <span>SEALED CACHE LOCATED</span>
            <strong>Ancient power is waking</strong>
          </div>
        )}

        {relaunch?.type === 'powerUp' && (
          <div
            className="coreboundPowerUpStatus"
            role="status"
            aria-live="polite"
          >
            <span>
              POWER UP
              {relaunch.multiplier > 1 ? ` ×${relaunch.multiplier}` : ''} · +
              {getPowerUpBonusPercent(relaunch.multiplier)}%
            </span>
            <strong>
              {relaunch.before} → {relaunch.after}
            </strong>
            <small>
              {relaunchStatus?.stage === 'landed'
                ? 'New landing locked'
                : relaunchStatus?.stage === 'flight'
                ? `Tile rebuilt as POWER UP ×${rebuiltPowerUpMultiplier} · Die relaunched`
                : relaunchStatus?.stage === 'blast'
                ? 'ENERGY BLAST'
                : 'Charging energy…'}
            </small>
          </div>
        )}
        {relaunch?.type === 'gambit' && (
          <div
            className="coreboundGambitRelaunchStatus"
            role="status"
            aria-live="polite"
          >
            <span>GAMBIT · ONE MORE THROW</span>
            <strong>
              {relaunch.before} → {relaunch.after}
            </strong>
            <small>
              {relaunchStatus?.stage === 'landed'
                ? 'New landing locked'
                : relaunchStatus?.stage === 'flight'
                ? 'Die relaunched · new landing incoming'
                : relaunchStatus?.stage === 'blast'
                ? 'ROLL SHATTER'
                : 'Shattering the die…'}
            </small>
          </div>
        )}
      </div>
      {shieldNotice}
      {trapEffect && (
        <div
          className={`coreboundTrapFeedback is-${getTrapVariant(trapEffect)}`}
          role="status"
        >
          <strong>TRAP · TURN SKIPPED</strong>
          <span>
            {getTrapVariant(trapEffect) === 'stun'
              ? 'Die stunned'
              : getTrapVariant(trapEffect) === 'immobility'
              ? 'Die immobilized'
              : 'Die frozen'}{' '}
            · Your attack is canceled.
          </span>
          <small>
            {sector === 1
              ? 'No enemy is present.'
              : 'A surviving enemy attacks twice before your next throw.'}
          </small>
        </div>
      )}
      {coreBoss && (
        <>
          {coreBoss.hitSplat && <DamageHitSplat {...coreBoss.hitSplat} />}
          {!webglAvailable && (
            <GlitchCoreScene
              forceFallback
              className="coreboundBoardCoreFallback"
              stage="ready"
              corePhase={coreBoss.phase}
              bossHp={coreBoss.hp}
              showPhaseBadge={false}
              decorative
            />
          )}
        </>
      )}
      {encounter && (
        <>
          <span
            ref={encounter.rewardOriginRef}
            className="coreboundGuardianRewardOrigin"
            aria-hidden="true"
          />
        </>
      )}
      {encounter?.combat &&
        !encounter.combat.frozen &&
        encounter.combat.stage !== 'launch' && (
          <DamageHitSplat
            key={encounter.combat.combatId}
            hitId={encounter.combat.combatId}
            damage={
              encounter.combat.effectiveRoll ?? encounter.combat.attackDealt
            }
            critical={
              encounter.combat.boardLanding?.effect?.type === 'critical'
            }
            target={encounter.enemy?.name || 'Minion'}
            reduceMotion={
              !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
            }
          />
        )}
      {encounter?.combat?.stage === 'counter' &&
        encounter.combat.retaliation && (
          <>
            {encounter.combat.damage > (encounter.combat.boardDamage || 0) && (
              <DamageHitSplat
                key={`incoming-${encounter.combat.combatId}`}
                hitId={`incoming-${encounter.combat.combatId}`}
                damage={
                  encounter.combat.damage - (encounter.combat.boardDamage || 0)
                }
                target="You"
                incoming
                reduceMotion={
                  !!window.matchMedia?.('(prefers-reduced-motion: reduce)')
                    .matches
                }
              />
            )}
            <div
              className={
                'coreboundGuardianRetaliation' +
                (encounter.combat.damage ? ' is-damage' : ' is-blocked')
              }
              role="status"
            >
              <strong>
                {encounter.combat.damage
                  ? '−' + encounter.combat.damage + ' HP'
                  : 'RETALIATION BLOCKED'}
              </strong>
              <span>
                Your HP: {encounter.combat.playerHpAfter} /{' '}
                {encounter.combat.playerMaxHp}
              </span>
            </div>
          </>
        )}
      <span
        className="coreboundRollNumber coreboundWebglRollLabel"
        aria-hidden="true"
      >
        {rollLabel}
      </span>
      {!webglAvailable &&
        ['guard', 'overload'].includes(attackStyle) &&
        !concealFacesUntilSettled && (
          <span
            className={`coreboundAttackStyleAuraFallback is-${attackStyle}`}
            aria-hidden="true"
          />
        )}
      {displayedShieldAmount > 0 && (
        <>
          {!webglAvailable && (
            <svg
              className="coreboundShieldFallback"
              viewBox="0 0 24 26"
              aria-hidden="true"
              focusable="false"
            >
              <path d="M12 2Q17 5 22 5L21 14Q20 21 12 25Q4 21 3 14L2 5Q7 5 12 2Z" />
              <path d="M12 2Q17 5 22 5L21 14Q20 21 12 25Q4 21 3 14L2 5Q7 5 12 2Z" />
            </svg>
          )}
          {!encounter && (!coreBoss || coreBoss.hideReadout) && shieldNotice}
        </>
      )}
      {!webglAvailable && slingshotPreview && doorBoard?.length === 25 && (
        <div
          className="coreboundSlingshotFallbackBoard"
          role="grid"
          aria-label="Slingshot board preview"
          aria-hidden={setupTileControlRef ? true : undefined}
        >
          {doorBoard.map((square, index) => (
            <span
              key={index}
              role="gridcell"
              aria-label={getBoardSquareLabel(square)}
              title={getBoardSquareLabel(square)}
              className={`${
                slingshotPreview.active &&
                index ===
                  getSlingshotPreviewTile(slingshotPreview, boardCondition)
                  ? 'is-target'
                  : ''
              } ${
                index === portalPair?.entryIndex
                  ? 'is-portal-a'
                  : index === portalPair?.exitIndex
                  ? 'is-portal-b'
                  : ''
              } ${index === bountyTagIndex ? 'is-bounty-tag' : ''} ${
                chainReactionReady &&
                slingshotPreview.active &&
                getChainReactionIndices(
                  getSlingshotPreviewTile(slingshotPreview, boardCondition)
                ).includes(index)
                  ? 'is-chain-preview'
                  : ''
              } ${icyTiles.includes(index) ? 'is-icy-tile' : ''}`}
            >
              {index === bountyTagIndex && (
                <small className="coreboundBountyTileLabel">BOUNTY +3</small>
              )}
              {index === portalPair?.entryIndex && <small>A</small>}
              {index === portalPair?.exitIndex && <small>B</small>}
              {isBoardEffectTile(square) ? (
                <BoardEffectTile effect={square.effect} />
              ) : (
                getBoardSquareLabel(square)
              )}
            </span>
          ))}
        </div>
      )}
      {!webglAvailable &&
        !slingshotPreview &&
        (concealFacesUntilSettled ? (
          <CoreDieFallback
            phase={corePhase}
            value={rollLabel}
            travelling={phase === 'rolling'}
          />
        ) : (
          <span className="coreboundWebglFallbackMessage">
            3-D signal unavailable
          </span>
        ))}
      {showBumpControls && (
        <div
          className={`coreboundBoardBumpControls ${
            showAnchorControls ? 'has-phase-anchor' : ''
          } ${ghostThrow ? 'is-ghost' : ''}`}
        >
          {showAnchorControls && (
            <div
              className="coreboundPhaseAnchorFlight"
              style={{ '--anchor-duration': `${PHASE_ANCHOR_DURATION_MS}ms` }}
            >
              <div role="status" aria-live="polite">
                <strong>
                  {anchorStatus.active ? 'TIME SLOWED' : 'PHASE ANCHOR'}
                </strong>
                <small>
                  {anchorStatus.active
                    ? 'Choose your next bump'
                    : anchorStatus.used
                    ? 'Used this floor'
                    : 'Press A · One use this floor'}
                </small>
                {anchorStatus.active && (
                  <span
                    className="coreboundPhaseAnchorTimer"
                    aria-hidden="true"
                  >
                    <span />
                  </span>
                )}
              </div>
              <button
                type="button"
                className="coreboundPhaseAnchorButton"
                disabled={
                  anchorStatus.used || !phaseAnchorArmed || !bumpStatus.active
                }
                onClick={() => phaseAnchorHandlerRef.current?.()}
              >
                {anchorStatus.active
                  ? 'Anchor active'
                  : anchorStatus.used
                  ? 'Anchor used'
                  : 'Activate Anchor'}
              </button>
            </div>
          )}
          <div
            className="coreboundBoardBumpCount"
            role="status"
            aria-live="polite"
          >
            <strong>
              {bumpStatus.remaining} / {bumpLimit}{' '}
              {ghostThrow ? 'ghost bump' : 'bumps left'}
            </strong>
            <span className="coreboundBoardBumpPips" aria-hidden="true">
              {Array.from({ length: bumpLimit }, (_, index) => (
                <i
                  key={index}
                  className={index < bumpStatus.remaining ? '' : 'is-used'}
                />
              ))}
            </span>
            <small>
              {!bumpStatus.active
                ? 'Die settled'
                : bumpStatus.remaining === 0
                ? ghostThrow
                  ? 'Used for this Core fight'
                  : 'All bumps used'
                : 'Tap toward a tile · Space to bump'}
            </small>
          </div>
          <button
            type="button"
            className="coreboundBoardBumpButton"
            disabled={!bumpStatus.active || bumpStatus.remaining === 0}
            aria-label={bumpButtonLabel}
            onClick={() => bumpHandlerRef.current?.()}
            onKeyDown={(event) => {
              if (event.key !== ' ' && event.key !== 'Enter') return;
              event.preventDefault();
              if (!event.repeat) bumpHandlerRef.current?.();
            }}
          >
            {ghostThrow
              ? ghostBumpUsed
                ? 'GHOST USED'
                : 'GHOST BUMP'
              : 'BUMP BOARD'}
          </button>
        </div>
      )}
    </div>
  );
};

export default GlitchPrismScene;
